import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCurrencySymbol, formatCurrency, formatCompactCurrency } from '../utils/currency';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import ContactEditModal from '../components/contacts/ContactEditModal';  // iter971k-Z · Turn 4
import { ContactTaxProfilePrompt } from '../components/contacts/ContactTaxProfilePrompt';
import EditInvoiceStatusBanner from '../components/invoice/EditInvoiceStatusBanner';  // iter971k-Z · Turn 5
import ProductEditModal from '../components/products/ProductEditModal';  // iter971k-Z · Turn 5
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FormattedNumberInput } from '../components/ui/formatted-number-input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { CurrencyInput, formatDisplayNumber } from '../components/ui/currency-input';
import { useNavigationContext } from '../hooks/useNavigationContext';
import { toast } from 'sonner';
import { format, addDays, parseISO } from 'date-fns';
import { ProjectSelect } from '../components/projects/ProjectSelect';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CalendarIcon, Loader2, Plus, Trash2, Check, Package, Briefcase, Sparkles, Building, Upload, FileText, X, Send, Save, CheckCircle2, Image as ImageIcon, DollarSign, Wand2, Brain, Zap, PenLine, AlertCircle, CreditCard, Link2, ExternalLink, Scissors, Clock   , Info, Lock } from 'lucide-react';
import AddBankAccountModal from '../components/AddBankAccountModal';
import { AISmartInput } from '../components/ai';
import FXRatePicker from '../components/common/FXRatePicker';
import SpecialItemModal, { getSpecialItemType } from '../components/SpecialItemModal';
import { AccountingStartDateModal } from '../components/shared/AccountingStartDateModal';
import { useDuplicateCheck } from '../components/DuplicateWarningDialog';
import { usePeriodLockCheck } from '../hooks/usePeriodLockCheck';
import { PeriodLockWarningDialog } from '../components/bankfeed/PeriodLockWarningDialog';
import NewAccountModal from '../components/accounting/NewAccountModal';
import { useTaxGateOpen } from '../utils/taxGateState';
import { BankTxActions } from '../components/bankfeed/BankTxActions';

const CreateInvoicePage = ({
  // Embedded mode (e.g. opened inside Bank Feed dialog).  When true:
  //   • The component skips its own page-level chrome (back button etc.)
  //   • After successful save, calls onComplete(invoiceId) instead of
  //     navigating to /invoices.
  //   • Prefill comes from prefillOverride prop (falls back to
  //     location.state / sessionStorage when null).
  embedded = false,
  prefillOverride = null,
  onComplete = null,
  onCancel = null,
  // iter971k-X · Edit-mode. When set, the page fetches this invoice, hydrates
  // all state, and switches Save from POST → PUT so the same form handles
  // both Create and Edit end-to-end (Turn 2 · Edit=Create parity).
  editInvoiceId: editInvoiceIdProp = null,
}) => {
  const { business } = useAuth();
  const navigateRouter = useNavigate();
  const taxGateOpen = useTaxGateOpen();
  const location = useLocation();
  const { id: routeId } = useParams();
  // iter971k-X - editInvoiceId comes from either the prop (rare, e.g. tests) or
  // the /invoices/:id/edit route param via useParams(). Only active when NOT
  // embedded (embedded mode is create-only inside the Bank Feed dialog).
  const editInvoiceId = !embedded ? (editInvoiceIdProp || routeId || null) : null;
  const isEditMode = !!editInvoiceId;

  // iter 1011 - reserve & display the next invoice number when the form opens.
  // The backend atomically increments a counter, so two forms open at the same
  // time can never show the same number. Users may edit it (uniqueness enforced on save).
  useEffect(() => {
    if (isEditMode) return;
    let cancelled = false;
    axios.get(`${API}/invoices/next-number`)
      .then((res) => {
        if (!cancelled && res.data?.number) {
          setFormData((prev) => (prev.invoiceNumber ? prev : { ...prev, invoiceNumber: res.data.number }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEditMode]);
  // When embedded we shim navigate so post-save / cancel actions close
  // the host dialog instead of routing away.
  const navigate = embedded
    ? (path) => {
        if (typeof path === 'string' && (path.startsWith('/invoices') || path.startsWith('/bank-feeds'))) {
          onComplete?.();
        }
      }
    : navigateRouter;
  const [loading, setLoading] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(new Date()); // Invoice date defaults to today
  const [invoiceProjectId, setInvoiceProjectId] = useState(null);  // iter 1003: project dimension
  const [dueDate, setDueDate] = useState(addDays(new Date(), 30));
  const [productsServices, setProductsServices] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccountsLoaded, setBankAccountsLoaded] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState('');
  // iter1225c - in-place bank enrichment modal for international invoices.
  // Not blocking: users can dismiss the nudge and send anyway.
  const [enrichBank, setEnrichBank] = useState(null);
  const [dismissedEnrichNudge, setDismissedEnrichNudge] = useState(false);
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const docInputRef = useRef(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showForceBankModal, setShowForceBankModal] = useState(false);
  const imageInputRefs = useRef({});
  
  // Accounting Start Date Modal state
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  
  // Duplicate transaction check
  const { checkForDuplicates, DuplicateDialog } = useDuplicateCheck();
  
  // Period lock check for bank feed transactions
  const { checkPeriodLock, periodLockDialog, clearPeriodLock } = usePeriodLockCheck();
  const periodLockOverrideRef = useRef(false);
  const periodLockOverrideDateRef = useRef(null);
  const pendingSubmitActionRef = useRef('save');
  
  // AI Mode state
  const [showAIMode, setShowAIMode] = useState(true); // Start with AI mode visible
  
  // Tax configuration state
  const [taxConfig, setTaxConfig] = useState(null);
  const [enableTax, setEnableTax] = useState(true);
  const [suggestedTaxes, setSuggestedTaxes] = useState({}); // Map of lineItemId -> suggested tax code
  const [taxDecisions, setTaxDecisions] = useState({}); // Phase C · lineItemId -> decide_tax explanation
  const [fetchingSuggestions, setFetchingSuggestions] = useState(false);
  
  // Revenue GL suggestion state
  const [revenueAccounts, setRevenueAccounts] = useState([]);
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false);
  const [glSuggestions, setGlSuggestions] = useState({}); // Map of lineItemId -> { code, name, suggestNew, newName }
  
  // Partial payment state
  const [showPartialPaymentDialog, setShowPartialPaymentDialog] = useState(false);
  const [partialPaymentAmount, setPartialPaymentAmount] = useState('');
  const [partialPaymentRef, setPartialPaymentRef] = useState('');
  const [partialPaymentDate, setPartialPaymentDate] = useState(new Date()); // iter971k-Z · Turn 5c
  const [recordingPartialPayment, setRecordingPartialPayment] = useState(false);
  // iter971k-Z · Turn 5c - Confirm-payment-date dialog for Mark Paid.
  // Every payment-recording action funnels through this before the GL post.
  const [showPaymentDateDialog, setShowPaymentDateDialog] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState(null); // 'mark_paid' | 'partial_payment' | ...
  const [confirmedPaymentDate, setConfirmedPaymentDate] = useState(new Date());

  // Bank Feed Transaction completion state
  const [bankFeedTransactionId, setBankFeedTransactionId] = useState(null);
  const [originalBankFeedStatus, setOriginalBankFeedStatus] = useState(null);
  const [isFromBankFeed, setIsFromBankFeed] = useState(false);
  
  // Split Transaction state
  const [isSplitMode, setIsSplitMode] = useState(false);
  const [splitExpectedTotal, setSplitExpectedTotal] = useState(0);
  
  // "Not Quite Right" mode state - locks amount and prevents adding new lines
  const [isNotQuiteRightMode, setIsNotQuiteRightMode] = useState(false);
  const [lockedAmount, setLockedAmount] = useState(0);

  // NEW: Special Item Modal State (for Deferred Revenue primarily on invoices)
  const [showSpecialItemModal, setShowSpecialItemModal] = useState(false);
  const [specialItemType, setSpecialItemType] = useState(null);
  const [currentSpecialItemLineId, setCurrentSpecialItemLineId] = useState(null);
  const [specialItemTerms, setSpecialItemTerms] = useState({});
  const [specialItemQueue, setSpecialItemQueue] = useState([]);

  // FX Rate state
  const [invoiceCurrency, setInvoiceCurrency] = useState(null);
  const [fxRate, setFxRate] = useState(1);
  const [fxRateSource, setFxRateSource] = useState('live');

  // Payment provider state
  const [paymentProviders, setPaymentProviders] = useState({ stripe: false });
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState('bank_transfer');
  // iter928 - Backend-driven payment-provider eligibility for this country
  const [countryProviders, setCountryProviders] = useState({

    stripe_supported: false,

    primary_provider: 'bank_transfer',
  });
  const [loadingPaymentProviders, setLoadingPaymentProviders] = useState(true);
  const [previousBankId, setPreviousBankId] = useState(null);

  // WHT Receivable state (when customer is a WHT agent)
  const [customerIsWhtAgent, setCustomerIsWhtAgent] = useState(false);
  const [whtExpectedHint, setWhtExpectedHint] = useState(null);
  const [whtRate, setWhtRate] = useState(10.0);  // Default 10% for corporate
  const whtRateTouchedRef = useRef(false); // user picked a rate manually → engine stops overriding
  const [whtAmount, setWhtAmount] = useState(0);
  const [showWhtSection, setShowWhtSection] = useState(false);
  const [aiSuggestingWht, setAiSuggestingWht] = useState(false);
  
  // Early Payment Discount state (for 2/10 Net 30 type incentives)
  const [enableDiscount, setEnableDiscount] = useState(false);
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' or 'fixed_amount'
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountDays, setDiscountDays] = useState('10');
  
  // Sales Discount state (trade discount applied at invoice creation - reduces invoice amount)
  const [enableSalesDiscount, setEnableSalesDiscount] = useState(false);
  const [salesDiscountType, setSalesDiscountType] = useState('percentage'); // 'percentage' or 'fixed_amount'
  const [salesDiscountPercent, setSalesDiscountPercent] = useState('');
  const [salesDiscountAmount, setSalesDiscountAmount] = useState('');
  const [salesDiscountReason, setSalesDiscountReason] = useState('');
  
  // VAT Mode state (exclusive = default for invoices, user enters face value)
  // Exclusive: User enters face value, VAT added on top → Gross = Face + VAT
  // Inclusive: User enters gross, VAT is inside → Face = Gross / (1 + VAT Rate)
  const [vatMode, setVatMode] = useState('exclusive'); // 'exclusive' or 'inclusive'
  // Tax setup → default price display for NEW invoices (bank-feed prefills stay locked inclusive; edits keep the document's mode)
  useEffect(() => {
    if (prefillOverride || location.state?.bankFeedPrefill) return;
    axios.get(`${API}/tax-setup/status`).then((r) => {
      if (r.data?.complete && r.data?.price_display) setVatMode(r.data.price_display);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Customer contact search state
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  // iter959 - debounce customer search HTTP calls
  const customerSearchTimerRef = useRef(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [contactEditOpen, setContactEditOpen] = useState(false);  // iter971k-Z · Turn 4
  // iter971k-Z · Turn 5 - Inline "Edit Product" from line items
  const [productEditOpen, setProductEditOpen] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingProductLineId, setEditingProductLineId] = useState(null);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    description: '',
    invoiceNumber: '',  // iter971k-W · user-editable override. Blank → server auto-generates INV-#####.
    lineItems: [{ id: 1, description: '', item_description: '', amount: '', quantity: 1, productId: null, isNew: false, image: null, uploadingImage: false, createRevenueGL: false, taxCode: '', taxRate: 0, revenueGlCode: null }]
  });

  const getLocalCurrencySymbol = (currency = null) => {
    // Use explicitly passed currency, then invoiceCurrency state, then derive from business currency
    const curr = currency || invoiceCurrency || business?.currency || 'NGN';
    return getCurrencySymbol(curr);
  };

  // Get the business's BASE currency
  const getBaseCurrency = () => {
    return business?.currency || 'NGN';
  };
  
  // Get the current working currency (invoice currency if set, otherwise base currency)
  const getCurrency = () => {
    return invoiceCurrency || getBaseCurrency();
  };

  // Eagerly load revenue accounts so the GL dropdown is always populated
  const fetchRevenueAccounts = async () => {
    try {
      const res = await axios.get(`${API}/accounting/chart-of-accounts`);
      const accounts = (res.data || []).filter(a => a.type === 'revenue' && !a.is_contra);
      if (accounts.length > 0) {
        setRevenueAccounts(accounts.map(a => ({ code: a.code, name: a.name })));
      }
    } catch (err) {
      console.log('Revenue accounts fetch error:', err);
    }
  };

  useEffect(() => {
    fetchProductsServices();
    fetchBankAccounts();
    fetchTaxConfig();
    fetchPaymentProviders();
    fetchRevenueAccounts();
    
    // Check for AI-prefilled data from sessionStorage
    const prefillData = sessionStorage.getItem('ai_prefill_invoice');
    if (prefillData) {
      try {
        const data = JSON.parse(prefillData);
        if (data.prefilled_from_ai) {
          // Populate form with AI data
          setFormData(prev => ({
            ...prev,
            customerName: data.customer_name || '',
            customerEmail: data.customer_email || '',
            description: data.notes || '',
            lineItems: data.items && data.items.length > 0 
              ? data.items.map((item, idx) => ({
                  id: idx + 1,
                  description: item.description || '',
                  amount: item.amount?.toString() || '',
                  quantity: item.quantity || 1,
                  productId: null,
                  isNew: true,
                  image: null,
                  uploadingImage: false,
                  createRevenueGL: false,
                  taxCode: '',
                  taxRate: 0,
                  revenueGlCode: null
                }))
              : [{ id: 1, description: '', amount: '', quantity: 1, productId: null, isNew: false, image: null, uploadingImage: false, createRevenueGL: false, taxCode: '', taxRate: 0, revenueGlCode: null }]
          }));
          
          // Set due date if provided
          if (data.due_date) {
            setDueDate(new Date(data.due_date));
          }
          
          // Show toast to inform user
          toast.success('Invoice form pre-filled from AI! Review and make any changes before saving.', {
            duration: 5000
          });
          
          // Clear the sessionStorage
          sessionStorage.removeItem('ai_prefill_invoice');
        }
      } catch (error) {
        console.error('Error parsing AI prefill data:', error);
        sessionStorage.removeItem('ai_prefill_invoice');
      }
    }
    
    // Check for bank feed prefill data - prop wins, then location.state.
    const bankFeedPrefill = prefillOverride || location.state?.bankFeedPrefill;
    const isSplitTransaction = location.state?.splitTransaction || bankFeedPrefill?.split_transaction;
    
    if (bankFeedPrefill) {
      // For split transactions, start with empty line items so user can add multiple lines
      // For regular bank feed, prefill with single line
      if (isSplitTransaction) {
        setFormData(prev => ({
          ...prev,
          customerName: bankFeedPrefill.counterparty || '',
          description: bankFeedPrefill.description || '',
          lineItems: [{
            id: 1,
            description: '',
            amount: '',
            quantity: 1,
            productId: null,
            isNew: true,
            image: null,
            uploadingImage: false,
            createRevenueGL: false,
            taxCode: '',
            taxRate: 0,
            revenueGlCode: ''
          }]
        }));
        
        // Store the expected total for split validation
        setSplitExpectedTotal(Math.abs(bankFeedPrefill.amount || 0));
        setIsSplitMode(true);
      } else {
        // "Not Quite Right" mode - single line with full amount LOCKED
        // User can change revenue GL code and description, but NOT the amount
        const prefillAmount = Math.abs(bankFeedPrefill.amount || 0);
        setFormData(prev => ({
          ...prev,
          customerName: bankFeedPrefill.counterparty || '',
          description: bankFeedPrefill.description || '',
          lineItems: [{
            id: 1,
            description: bankFeedPrefill.description || 'Bank Feed Transaction',
            amount: prefillAmount.toString(),
            quantity: 1,
            productId: null,
            isNew: true,
            image: null,
            uploadingImage: false,
            createRevenueGL: false,
            taxCode: '',
            taxRate: 0,
            revenueGlCode: bankFeedPrefill.gl_credit || null
          }]
        }));
        
        // Enable "Not Quite Right" mode - locks amount and prevents adding lines
        setIsNotQuiteRightMode(true);
        setLockedAmount(prefillAmount);
      }
      
      if (bankFeedPrefill.date) {
        setInvoiceDate(new Date(bankFeedPrefill.date));
      }
      
      if (bankFeedPrefill.bank_account_id) {
        setSelectedBankId(bankFeedPrefill.bank_account_id);
      }
      
      // Track bank feed transaction ID for completion
      if (bankFeedPrefill.bank_feed_transaction_id) {
        setBankFeedTransactionId(bankFeedPrefill.bank_feed_transaction_id);
        setOriginalBankFeedStatus(bankFeedPrefill.original_status || null);
        setIsFromBankFeed(true);
      }
      // P2 · the locked amount is in the BANK account's currency → document currency follows it
      if (bankFeedPrefill.currency && bankFeedPrefill.currency.toUpperCase() !== getBaseCurrency()) {
        setInvoiceCurrency(bankFeedPrefill.currency.toUpperCase());
      }
      
      // CRITICAL: Force VAT to inclusive mode for bank feed transactions
      // The bank amount is sacred - VAT must be computed WITHIN it, not added on top
      setVatMode('inclusive');
      
      // Different toast messages for split vs reclassify
      if (isSplitTransaction) {
        toast.info('Split Transaction Mode: Add multiple line items. Total must equal the original transaction amount.', {
          duration: 8000
        });
      } else {
        toast.info('Form pre-filled from bank transaction. Amount, date, and bank account are locked to match the bank record.', {
          duration: 5000
        });
      }
    }
  }, [location.state]);

  // iter971k-X · Edit-mode hydration. When the URL is /invoices/:id/edit, load
  // the existing invoice and populate every state variable so this same form
  // renders in "Edit" mode with full parity to Create. On save, we PUT instead
  // of POST. Runs once on mount when editInvoiceId is set.
  const [editLoaded, setEditLoaded] = useState(false);
  const [existingInvoice, setExistingInvoice] = useState(null);

  // iter971k-Z · Turn 5c - Freeze all financial fields when the invoice has
  // recorded payments, is reconciled, or is written-off/cancelled.  Non-
  // financial fields (customer name/email, description, notes, supporting
  // docs) stay editable.  Users must remove/void payments (or unreconcile
  // the bank transaction) before touching financial data.
  const financeLocked = Boolean(
    isEditMode && existingInvoice && (
      Number(existingInvoice.amount_paid || 0) > 0
      || ['paid', 'partial', 'written_off', 'cancelled', 'reconciled'].includes(String(existingInvoice.status || '').toLowerCase())
      || existingInvoice.is_reconciled
      || existingInvoice.reconciliation_id
      || existingInvoice.bank_feed_transaction_id
      || existingInvoice.bank_feed_tx_id
    )
  );
  const bankFeedLocked = Boolean(isEditMode && existingInvoice && (existingInvoice.bank_feed_transaction_id || existingInvoice.bank_feed_tx_id || existingInvoice.bank_feed_link_ids?.length || existingInvoice.bank_feed_source));
  const financeLockClass = financeLocked ? 'pointer-events-none opacity-70 select-none' : '';
  useEffect(() => {
    if (!isEditMode || editLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: inv } = await axios.get(`${API}/invoices/${editInvoiceId}`);
        if (cancelled) return;
        setExistingInvoice(inv);
        setInvoiceProjectId(inv.project_id || null);
        // Populate the form scaffolding
        setFormData({
          customerName: inv.customer_name || '',
          customerEmail: inv.customer_email || '',
          description: inv.description || '',
          invoiceNumber: inv.invoice_number || '',
          lineItems: (inv.line_items && inv.line_items.length > 0)
            ? inv.line_items.map((li, idx) => ({
                id: idx + 1,
                description: li.description || '',
                item_description: li.item_description || '',
                amount: (li.unit_price ?? li.amount)?.toString() || '',
                quantity: li.quantity || 1,
                productId: li.product_id || null,
                isNew: false,
                image: li.image_url || null,
                uploadingImage: false,
                matchedProduct: li.product_id ? { id: li.product_id, name: li.description, revenue_gl_code: li.revenue_account_code } : null,
                createRevenueGL: false,
                revenueGlCode: li.revenue_account_code || null,
                revenueGlName: li.revenue_account_name || null,
                projectId: li.project_id || null,
                taxCode: li.tax_code || 'NONE',
                taxRate: li.tax_rate || 0,
              }))
            : [{ id: 1, description: inv.description || '', amount: inv.amount?.toString() || '', quantity: 1, productId: null, isNew: false, image: null, uploadingImage: false, matchedProduct: null, createRevenueGL: false, revenueGlCode: null, taxCode: 'NONE', taxRate: 0 }],
        });
        // Dates
        if (inv.invoice_date) setInvoiceDate(parseISO(inv.invoice_date));
        if (inv.due_date) setDueDate(parseISO(inv.due_date));
        // Currency + FX
        if (inv.currency) setInvoiceCurrency(inv.currency);
        if (inv.fx_rate) setFxRate(Number(inv.fx_rate) || 1);
        if (inv.fx_rate_source) setFxRateSource(inv.fx_rate_source);
        // Bank + docs
        if (inv.bank_account_id) setSelectedBankId(inv.bank_account_id);
        if (inv.supporting_documents?.length > 0) {
          setSupportingDocs(inv.supporting_documents.map((docId, idx) => ({
            id: docId, filename: `Document ${idx + 1}`, file_type: 'pdf',
          })));
        }
        // VAT + WHT
        if (inv.vat_mode) setVatMode(inv.vat_mode);
        const hadWht = !!(inv.customer_is_wht_agent || inv.client_will_deduct_wht);
        setCustomerIsWhtAgent(hadWht);
        if (hadWht && inv.wht_rate) setWhtRate(Number(inv.wht_rate) || 10.0);
        setShowWhtSection(hadWht);
        // Sales discount (trade)
        const hadSalesDiscount = !!(inv.enable_sales_discount || inv.sales_discount_amount || inv.sales_discount_applied || inv.sales_discount_percent);
        setEnableSalesDiscount(hadSalesDiscount);
        if (hadSalesDiscount) {
          setSalesDiscountType(inv.sales_discount_type || 'percentage');
          if (inv.sales_discount_percent != null) setSalesDiscountPercent(String(inv.sales_discount_percent));
          if (inv.sales_discount_amount != null) setSalesDiscountAmount(String(inv.sales_discount_amount));
          setSalesDiscountReason(inv.sales_discount_reason || '');
        }
        // Early-payment discount
        if (inv.enable_early_payment_discount) {
          setEnableDiscount(true);
          setDiscountType(inv.discount_type || 'percentage');
          if (inv.discount_percent != null) setDiscountPercent(String(inv.discount_percent));
          if (inv.discount_amount != null) setDiscountAmount(String(inv.discount_amount));
          if (inv.discount_days != null) setDiscountDays(String(inv.discount_days));
        }
        // Payment provider
        if (inv.payment_provider) setSelectedPaymentProvider(inv.payment_provider);
        setEditLoaded(true);
      } catch (err) {
        toast.error('Failed to load invoice');
        navigateRouter('/invoices');
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editInvoiceId, isEditMode]);

  // Debounced tax suggestion fetch when line items change
  // Using 150ms debounce for quick response while user is typing
  const suggestionsCache = useRef(new Map());
  const lastFetchedDescriptions = useRef(new Set());
  
  // Immediate fetch function (no debounce) - called on blur
  const fetchTaxSuggestionsImmediate = async (itemsToSuggest) => {
    if (!taxConfig || itemsToSuggest.length === 0) return;
    
    const cacheKey = itemsToSuggest.map(i => i.description.toLowerCase().trim()).join('|');
    const cached = suggestionsCache.current.get(cacheKey);
    
    if (cached) {
      setSuggestedTaxes(cached);
    } else {
      await fetchTaxSuggestions(itemsToSuggest);
    }
  };
  
  // Handle description blur - immediately fetch suggestions
  const handleDescriptionBlur = (item) => {
    if (item.description && item.description.trim().length >= 3) {
      const descKey = item.description.toLowerCase().trim();
      if (!lastFetchedDescriptions.current.has(descKey)) {
        lastFetchedDescriptions.current.add(descKey);
        fetchTaxSuggestionsImmediate([item]);
        fetchRevenueGlSuggestion(item);
      }
    }
  };
  
  // Fetch revenue GL suggestion for a line item (deterministic, no AI)
  const fetchRevenueGlSuggestion = async (item) => {
    if (!item.description || item.description.trim().length < 3) return;
    // Skip if user already manually selected a GL (and it's not the default 4000)
    if (item.revenueGlCode && item.revenueGlCode !== '4000' && !glSuggestions[item.id]) return;
    
    try {
      const res = await axios.post(`${API}/invoices/suggest-revenue-category`, {
        description: item.description.trim(),
        product_name: item.matchedProduct?.name || null
      });
      const data = res.data;
      setGlSuggestions(prev => ({
        ...prev,
        [item.id]: {
          code: data.suggested_gl_code,
          name: data.suggested_gl_name,
          suggestNew: data.suggest_new_gl,
          newName: data.suggested_new_gl_name,
          confidence: data.confidence
        }
      }));
      // Auto-apply suggestion if no GL is set yet or still on default
      if (!item.revenueGlCode || item.revenueGlCode === '4000') {
        updateLineItem(item.id, 'revenueGlCode', data.suggested_gl_code);
        updateLineItem(item.id, 'revenueGlName', data.suggested_gl_name);
      }
      // Merge available revenue accounts into state (avoid duplicates)
      if (data.available_revenue_accounts && data.available_revenue_accounts.length > 0) {
        setRevenueAccounts(prev => {
          const existing = new Set(prev.map(a => a.code));
          const merged = [...prev];
          for (const acc of data.available_revenue_accounts) {
            if (!existing.has(acc.code)) {
              merged.push(acc);
              existing.add(acc.code);
            }
          }
          return merged.sort((a, b) => a.code.localeCompare(b.code));
        });
      }
    } catch (err) {
      console.log('Revenue GL suggestion error:', err);
    }
  };
  
  useEffect(() => {
    const timer = setTimeout(() => {
      // Fetch suggestions for all items with descriptions of at least 3 chars
      // (regardless of whether they have a tax code - we want to show AI suggestions for comparison)
      // item name OR free-text description both identify the supply for the tax engine
      const itemsToSuggest = formData.lineItems
        .map((item) => ({ ...item, description: (item.description || item.item_description || '').trim() }))
        .filter((item) => item.description.length >= 3);
      
      if (taxConfig && itemsToSuggest.length > 0) {
        // Cache is keyed by items + customer (the customer changes exports / WHT expectation)
        const cacheKey = `${selectedCustomerId || ''}|` + itemsToSuggest.map(i => i.description.toLowerCase()).join('|');
        const cached = suggestionsCache.current.get(cacheKey);
        
        if (cached && !selectedCustomerId) {
          setSuggestedTaxes(cached);
        } else {
          fetchTaxSuggestions(itemsToSuggest);
        }
      }
    }, 400);
    
    return () => clearTimeout(timer);
  }, [formData.lineItems, taxConfig, selectedCustomerId]);

  const fetchProductsServices = async () => {
    try {
      const response = await axios.get(`${API}/products-services`);
      setProductsServices(response.data);
    } catch (error) {
      console.log('Loading products...', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts`);
      const usable = response.data;
      // iter1224 - order matters: set `usable` and mark loaded BEFORE
      // setSelectedBankId so that when React commits both updates in the
      // same batch, the SelectContent has items to match against and Radix
      // doesn't fire an "invalid value" reset via onValueChange('').
      setBankAccounts(usable);
      const payable = usable.filter(b => b.source !== 'stripe');
      if (payable.length === 0) {
        setBankAccountsLoaded(true);
        setShowForceBankModal(true);
      } else {
        const currency = getCurrency();
        const defaultBank = payable.find(b => b.currency === currency && b.is_default);
        const pick = defaultBank ? defaultBank.id : payable[0].id;
        setSelectedBankId(pick);
        setBankAccountsLoaded(true);
      }
    } catch (error) {
      console.log('Loading bank accounts...', error);
      setBankAccountsLoaded(true);
    }
  };

  const fetchTaxConfig = async () => {
    try {
      // Fetch from centralized tax management API
      const response = await axios.get(`${API}/tax-management/rules`);
      const rules = response.data?.rules || [];
      
      // Filter for output taxes (sales taxes) only for invoices
      const outputTaxes = rules.filter(r => r.tax_type === 'output' && r.is_active);
      
      // Convert to the format expected by the tax dropdown
      const taxes = outputTaxes.map(r => ({
        code: r.code,
        name: r.name,
        rate: r.rate,
        gl_account_code: r.gl_account_code,
        is_default: r.is_default
      }));
      
      setTaxConfig({ taxes });
      setEnableTax(true);
      
      // If no taxes, try to initialize them
      if (taxes.length === 0) {
        try {
          await axios.post(`${API}/tax-management/initialize`);
          // Re-fetch after initialization
          const refreshed = await axios.get(`${API}/tax-management/rules`);
          const refreshedRules = refreshed.data?.rules || [];
          const refreshedOutputTaxes = refreshedRules.filter(r => r.tax_type === 'output' && r.is_active);
          setTaxConfig({ 
            taxes: refreshedOutputTaxes.map(r => ({
              code: r.code,
              name: r.name,
              rate: r.rate,
              gl_account_code: r.gl_account_code,
              is_default: r.is_default
            }))
          });
        } catch (initError) {
          console.log('Tax initialization skipped:', initError);
        }
      }
    } catch (error) {
      console.log('Loading tax config...', error);
      // Fallback to old API if new one fails
      try {
        const fallback = await axios.get(`${API}/products-services/tax-config`);
        setTaxConfig(fallback.data?.config || null);
        setEnableTax(fallback.data?.enable_tax_management !== false);
      } catch (fallbackError) {
        console.log('Fallback tax config also failed');
      }
    }
  };

  // Fetch payment provider connection status
  const fetchPaymentProviders = async () => {
    setLoadingPaymentProviders(true);
    try {
      // iter928 - Stripe Connect status. Considered "connected" only
      // when the account is fully onboarded (details_submitted + charges_enabled).
      const stripeRes = await axios.get(`${API}/stripe-connect/status`).catch(() => ({ data: { connected: false } }));

      const stripeReady = !!(stripeRes.data?.connected
        && stripeRes.data?.details_submitted
        && stripeRes.data?.charges_enabled);

      const providers = { stripe: stripeReady };
      setPaymentProviders(providers);

      // Backend-driven provider allowlist for this business's country.
      try {
        const provRes = await axios.get(`${API}/payments/providers`);
        setCountryProviders({
          stripe_supported: !!provRes.data?.stripe_supported,
          primary_provider: provRes.data?.primary_provider || 'bank_transfer',
        });
      } catch (e) { /* keep defaults */ }

      if (providers.stripe) {
        setSelectedPaymentProvider('stripe');
      } else {
        setSelectedPaymentProvider('bank_transfer');
      }
    } catch (error) {
      console.log('Error fetching payment providers:', error);
    } finally {
      setLoadingPaymentProviders(false);
    }
  };

  // iter1224 - Restore the previous bank account when switching back to
  // bank transfer. Do NOT auto-clear when switching to Stripe: merchants can
  // still expose a bank-transfer fallback alongside the Stripe pay button.
  useEffect(() => {
    if (selectedPaymentProvider === 'bank_transfer' && previousBankId && !selectedBankId) {
      setSelectedBankId(previousBankId);
      setPreviousBankId(null);
    }
  }, [selectedPaymentProvider]);


  // Search customer contacts
  const searchCustomerContacts = async (query) => {
    if (!query || query.length < 2) {
      setCustomerSuggestions([]);
      return;
    }
    // iter959 - 300ms debounce so we don't fire an HTTP call on every keystroke.
    if (customerSearchTimerRef.current) clearTimeout(customerSearchTimerRef.current);
    customerSearchTimerRef.current = setTimeout(async () => {
      setSearchingCustomers(true);
      try {
        const response = await axios.get(`${API}/contacts/search`, {
          params: {
            q: query,
            contact_type: 'customer'
          }
        });
        setCustomerSuggestions(response.data?.contacts || []);
      } catch (error) {
        console.log('Error searching customers:', error);
        setCustomerSuggestions([]);
      } finally {
        setSearchingCustomers(false);
      }
    }, 300);
  };

  // Select a customer from suggestions
  const selectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerName: customer.name,
      customerEmail: customer.email || ''
    }));
    setSelectedCustomerId(customer.id);
    setCustomerSuggestions([]);
    
    // If customer has payment terms, auto-set due date
    if (customer.payment_terms) {
      setDueDate(addDays(new Date(), customer.payment_terms));
    }
  };

  // Fetch AI tax suggestions for line items with caching
  const fetchTaxSuggestions = async (lineItems) => {
    if (!lineItems || lineItems.length === 0) return;
    
    // Only fetch suggestions for items with descriptions
    const itemsWithDescriptions = lineItems.filter(item => item.description && item.description.trim().length >= 3);
    if (itemsWithDescriptions.length === 0) return;
    
    setFetchingSuggestions(true);
    try {
      // Build items array for suggestion API
      const items = itemsWithDescriptions.map(item => ({
        description: item.description.trim(),
        amount: parseFloat(item.amount) || 0,
        category: item.matchedProduct?.type || 'goods' // Default to goods if not specified
      }));
      
      const response = await axios.post(`${API}/tax-management/suggest`, {
        transaction_type: 'invoice',
        items: items.map((it, idx) => ({ ...it, product_id: itemsWithDescriptions[idx]?.matchedProduct?.id || itemsWithDescriptions[idx]?.productId || null })),
        contact_id: selectedCustomerId || null,
      });
      if (response.data?.decisions) {
        const why = {};
        itemsWithDescriptions.forEach((item, idx) => { if (response.data.decisions[idx]) why[item.id] = response.data.decisions[idx]; });
        setTaxDecisions((prev) => ({ ...prev, ...why }));
        // WHT receivable: the engine tells us whether this customer deducts WHT and at which pack rate
        const expected = response.data.decisions.find((d) => d?.wht?.expected);
        if (expected && !whtRateTouchedRef.current) {
          if (expected.wht.rate > 0) setWhtRate(Number(expected.wht.rate));
          // Sep 2026 · the customer's "deducts WHT" profile flag is a HINT only — WHT on this invoice is the user's explicit tick.
          setWhtExpectedHint(expected.wht.reason || 'This customer is marked as WHT-deducting.');
        } else if (!expected) {
          setWhtExpectedHint(null);
        }
      }
      
      if (response.data?.suggestions) {
        // Map suggestions to line item IDs
        const newSuggestions = {};
        itemsWithDescriptions.forEach((item, idx) => {
          if (response.data.suggestions[idx]) {
            const suggested = response.data.suggestions[idx];
            // Only suggest if it's a default tax
            const defaultTax = suggested.find(s => s.is_default);
            if (defaultTax) {
              newSuggestions[item.id] = defaultTax.code;
            } else if (suggested.length > 0) {
              newSuggestions[item.id] = suggested[0].code;
            }
          }
        });
        
        // Cache the result
        const cacheKey = `${selectedCustomerId || ''}|` + itemsWithDescriptions.map(i => i.description.toLowerCase().trim()).join('|');
        suggestionsCache.current.set(cacheKey, newSuggestions);
        
        // Limit cache size to 50 entries
        if (suggestionsCache.current.size > 50) {
          const firstKey = suggestionsCache.current.keys().next().value;
          suggestionsCache.current.delete(firstKey);
        }
        
        setSuggestedTaxes(newSuggestions);
        // Tax engine is definitive → apply it to lines the user has not taxed yet (same behaviour as bank-feed auto-post).
        // The user can still change the code on any line; tracking OFF returns no suggestions so nothing is applied.
        const decisions = response.data?.decisions || [];
        const autoCodes = {};
        itemsWithDescriptions.forEach((item, idx) => {
          const dec = decisions[idx];
          if (dec?.definitive && !dec.review_required && newSuggestions[item.id]) autoCodes[item.id] = newSuggestions[item.id];
        });
        if (Object.keys(autoCodes).length && taxConfig?.taxes?.length) {
          setFormData((prev) => ({
            ...prev,
            lineItems: prev.lineItems.map((li) => {
              const code = autoCodes[li.id];
              const t = code && taxConfig.taxes.find((x) => x.code === code);
              return t && (!li.taxCode || li.taxCode === 'NONE') ? { ...li, taxCode: code, taxRate: t.rate } : li;
            }),
          }));
        }
      }
    } catch (error) {
      console.log('Tax suggestions not available:', error);
    } finally {
      setFetchingSuggestions(false);
    }
  };

  // Apply AI suggested tax to a line item
  const applySuggestedTax = (itemId) => {
    const suggestedCode = suggestedTaxes[itemId];
    if (!suggestedCode || !taxConfig?.taxes) return;
    
    const suggestedTax = taxConfig.taxes.find(t => t.code === suggestedCode);
    if (suggestedTax) {
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map(item => 
          item.id === itemId 
            ? { ...item, taxCode: suggestedCode, taxRate: suggestedTax.rate }
            : item
        )
      }));
      toast.success(`Applied ${suggestedTax.name} (${suggestedTax.rate}%)`);
    }
  };

  // Apply AI suggested taxes to all applicable line items
  const applyAllSuggestedTaxes = () => {
    if (!taxConfig?.taxes || Object.keys(suggestedTaxes).length === 0) return;
    
    let appliedCount = 0;
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        const suggestedCode = suggestedTaxes[item.id];
        // Apply if suggestion exists and current tax doesn't match suggestion
        if (suggestedCode && item.taxCode !== suggestedCode) {
          const suggestedTax = taxConfig.taxes.find(t => t.code === suggestedCode);
          if (suggestedTax) {
            appliedCount++;
            return { ...item, taxCode: suggestedCode, taxRate: suggestedTax.rate };
          }
        }
        return item;
      })
    }));
    
    if (appliedCount > 0) {
      toast.success(`Applied suggested taxes to ${appliedCount} item${appliedCount > 1 ? 's' : ''}`);
    }
  };

  // Get available tax options based on business country
  const getTaxOptions = () => {
    if (!taxConfig || !taxConfig.taxes) return [];
    return [
      { code: 'NONE', name: 'No Tax', rate: 0 },
      ...taxConfig.taxes
    ];
  };

  // Calculate line item tax amount
  // In EXCLUSIVE mode: Tax = entered amount * tax rate
  // In INCLUSIVE mode: Tax = entered amount - (entered amount / (1 + tax rate))
  const calculateLineTax = (item) => {
    const lineGross = (parseFloat(item.amount) || 0) * (parseFloat(item.quantity) || 1);
    const taxRate = (item.taxRate || 0) / 100;
    
    if (vatMode === 'inclusive') {
      // In inclusive mode, tax is backed out of the entered amount
      const lineFaceValue = lineGross / (1 + taxRate);
      return lineGross - lineFaceValue;
    } else {
      // In exclusive mode, tax is added on top
      return lineGross * taxRate;
    }
  };
  
  // Calculate line item total (what displays in TOTAL column)
  // In EXCLUSIVE mode: Total = entered amount + tax (tax added on top)
  // In INCLUSIVE mode: Total = entered amount (tax is already inside)
  const calculateLineTotal = (item) => {
    const lineAmount = (parseFloat(item.amount) || 0) * (parseFloat(item.quantity) || 1);
    
    if (vatMode === 'inclusive') {
      // In inclusive mode, the entered amount IS the total (tax is inside)
      return lineAmount;
    } else {
      // In exclusive mode, add tax on top
      return lineAmount + calculateLineTax(item);
    }
  };
  
  // Calculate line item face value (pre-tax amount)
  // In EXCLUSIVE mode: Face Value = entered amount
  // In INCLUSIVE mode: Face Value = entered amount / (1 + tax rate)
  const calculateLineFaceValue = (item) => {
    const lineAmount = (parseFloat(item.amount) || 0) * (parseFloat(item.quantity) || 1);
    const taxRate = (item.taxRate || 0) / 100;
    
    if (vatMode === 'inclusive') {
      return lineAmount / (1 + taxRate);
    } else {
      return lineAmount;
    }
  };

  // Calculate subtotal (before tax) - this is the FACE VALUE
  // Uses calculateLineFaceValue for each line item
  const calculateSubtotal = () => {
    return formData.lineItems.reduce((sum, item) => {
      return sum + calculateLineFaceValue(item);
    }, 0);
  };

  // Calculate total tax (Output VAT)
  // Uses calculateLineTax for each line item (already VAT-mode aware)
  const calculateTotalTax = () => {
    return formData.lineItems.reduce((sum, item) => {
      return sum + calculateLineTax(item);
    }, 0);
  };
  
  // Get Face Value (for WHT calculation and revenue recognition)
  // WHT is ALWAYS calculated on FACE VALUE, never on VAT-inclusive amount
  const calculateFaceValue = () => {
    return calculateSubtotal(); // Subtotal IS the face value
  };
  
  // Get Gross Amount (total including VAT)
  const calculateGrossAmount = () => {
    return formData.lineItems.reduce((sum, item) => {
      return sum + calculateLineTotal(item);
    }, 0);
  };

  // Smart duplicate detection using fuzzy matching
  const findSimilarProduct = (name) => {
    if (!name || name.length < 2) return null;
    
    const searchName = name.toLowerCase().trim();
    
    // First try exact match
    const exactMatch = productsServices.find(p => 
      p.name.toLowerCase() === searchName
    );
    if (exactMatch) return exactMatch;
    
    // Then try contains match
    const containsMatch = productsServices.find(p => 
      p.name.toLowerCase().includes(searchName) || 
      searchName.includes(p.name.toLowerCase())
    );
    if (containsMatch) return containsMatch;
    
    // Fuzzy match - check if words match
    const searchWords = searchName.split(' ').filter(w => w.length > 2);
    const fuzzyMatch = productsServices.find(p => {
      const productWords = p.name.toLowerCase().split(' ');
      return searchWords.some(sw => productWords.some(pw => 
        pw.includes(sw) || sw.includes(pw)
      ));
    });
    
    return fuzzyMatch;
  };

  const addLineItem = () => {
    const newId = Math.max(...formData.lineItems.map(i => i.id), 0) + 1;
    // Get default tax rate from config (first tax option after "No Tax")
    const defaultTax = taxConfig?.taxes?.[0] || { code: 'NONE', rate: 0 };
    setFormData({
      ...formData,
      lineItems: [...formData.lineItems, { 
        id: newId, description: '', item_description: '', amount: '', quantity: 1, productId: null, isNew: false, 
        image: null, uploadingImage: false, createRevenueGL: false,
        taxCode: defaultTax.code || 'NONE', taxRate: defaultTax.rate || 0, revenueGlCode: null
      }]
    });
  };

  const removeLineItem = (id) => {
    if (formData.lineItems.length <= 1) return;
    setFormData({
      ...formData,
      lineItems: formData.lineItems.filter(item => item.id !== id)
    });
  };

  const updateLineItem = (id, field, value) => {
    // Functional update: rapid keystrokes / blur-format events from CurrencyInput must not overwrite each other with a stale closure.
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map(item => {
        if (item.id !== id) return item;
        
        const updated = { ...item, [field]: value };
        
        // When description changes, check for existing product
        if (field === 'description') {
          const similar = findSimilarProduct(value);
          if (similar) {
            updated.productId = similar.id;
            updated.amount = similar.price.toString();
            updated.isNew = false;
            updated.matchedProduct = similar;
          } else if (value.length > 2) {
            updated.productId = null;
            updated.isNew = true;
            updated.matchedProduct = null;
          }
        }
        
        return updated;
      })
    }));
  };

  const selectProduct = (lineItemId, product) => {
    // Determine default tax based on product's taxable setting
    // Phase B · the statutory supply category on the product wins: exempt / zero-rated / out-of-scope → no tax line
    const noTax = ['exempt', 'zero_rated', 'out_of_scope'].includes(product.tax_treatment) || product.taxable === false;
    const defaultTax = !noTax && taxConfig?.taxes?.[0]
      ? { code: taxConfig.taxes[0].code, rate: taxConfig.taxes[0].rate }
      : { code: 'NONE', rate: 0 };
    
    setFormData({
      ...formData,
      lineItems: formData.lineItems.map(item => {
        if (item.id !== lineItemId) return item;
        return {
          ...item,
          description: product.name,
          amount: product.price.toString(),
          productId: product.id,
          isNew: false,
          matchedProduct: product,
          // Use product's dedicated revenue GL code if it has one
          revenueGlCode: product.revenue_gl_code || null,
          // Apply tax based on product settings
          taxCode: defaultTax.code,
          taxRate: defaultTax.rate
        };
      })
    });
  };

  // Regional second tax (CA PST/RST/QST) — comes only from the tax engine's decision for this business's province
  const secondaryTaxInfo = Object.values(taxDecisions).find((d) => d?.secondary_tax?.applies)?.secondary_tax || null;
  const calculateSecondaryTax = () => {
    if (!secondaryTaxInfo) return 0;
    return formData.lineItems.reduce((sum, item) => sum + (((item.taxRate || 0) > 0) ? calculateLineFaceValue(item) * secondaryTaxInfo.rate / 100 : 0), 0);
  };

  const calculateTotal = () => {
    // Total is the gross amount (face value + VAT + any regional second tax)
    return calculateGrossAmount() + calculateSecondaryTax();
  };
  
  // Calculate Sales Discount amount
  const calculateSalesDiscount = () => {
    if (!enableSalesDiscount) return 0;
    const gross = calculateTotal();
    if (salesDiscountType === 'percentage' && salesDiscountPercent) {
      return gross * (parseFloat(salesDiscountPercent) / 100);
    } else if (salesDiscountType === 'fixed_amount' && salesDiscountAmount) {
      return Math.min(parseFloat(salesDiscountAmount) || 0, gross);
    }
    return 0;
  };
  
  // Calculate Net Total after sales discount
  const calculateNetTotal = () => {
    return calculateTotal() - calculateSalesDiscount();
  };

  // WHT Receivable calculations
  // IMPORTANT: WHT is calculated on FACE VALUE (subtotal), NOT on gross amount
  const calculateWhtAmount = () => {
    if (!customerIsWhtAgent || whtRate <= 0) return 0;
    const faceValue = calculateFaceValue(); // Use face value, NOT total
    return Math.round(faceValue * (whtRate / 100) * 100) / 100;
  };

  const calculateNetReceivable = () => {
    // Net amount customer will actually pay (gross minus Sales Discount minus WHT they withhold)
    // WHT is deducted from gross, but calculated on face value
    return calculateNetTotal() - calculateWhtAmount();
  };

  const [saveAction, setSaveAction] = useState('save'); // 'save', 'draft', 'send', 'mark_paid', 'stripe'

  const handleSubmit = async (e, action = 'save') => {
    e.preventDefault();
    
    // Check if accounting start date is set - block save if not
    if (business && !business.accounting_start_date) {
      setShowStartDateModal(true);
      return;
    }
    
    if (!formData.customerName.trim()) {
      toast.error('Please enter customer name');
      return;
    }
    
    // Every line carrying an amount is posted; a blank description falls back to the
    // GL / invoice description so a split never silently drops a line.
    const validItems = formData.lineItems
      .filter(item => parseFloat(item.amount) > 0 || (item.description && item.description.trim()))
      .map(item => ({ ...item, description: (item.description || '').trim() || item.glName || item.category || formData.description || 'Line item' }));
    if (validItems.length === 0) {
      toast.error('Please add at least one item with description and amount');
      return;
    }

    // For send action, require email
    if ((action === 'send' || action === 'stripe') && !formData.customerEmail) {
      toast.error('Please enter customer email to send invoice');
      return;
    }

    // For bank transfer, recommend bank account but don't require it
    // Users can update payment details later
    if (!selectedBankId && action !== 'stripe' && action !== 'draft') {
      // Just warn, don't block - bank account can be added later
      console.log('No bank account selected - invoice will be created without payment details');
    }

    // CRITICAL: Split transaction validation - line items must sum to expected total
    if (isSplitMode) {
      const totalAmount = calculateTotal();
      if (Math.abs(totalAmount - splitExpectedTotal) >= 0.01) {
        const remaining = Math.abs(splitExpectedTotal - totalAmount);
        toast.error(`Split transaction incomplete: Total must equal ${getLocalCurrencySymbol()}${formatDisplayNumber(splitExpectedTotal)}. Remaining: ${getLocalCurrencySymbol()}${formatDisplayNumber(remaining)}`);
        return;
      }
    }

    setLoading(true);
    setSaveAction(action);
    
    try {
      // Duplicate check before save (non-blocking on failure)
      if (action !== 'draft') {
        const totalAmount = calculateTotal();
        const dateStr = invoiceDate instanceof Date ? invoiceDate.toISOString().split('T')[0] : invoiceDate;
        const shouldProceed = await checkForDuplicates({
          amount: totalAmount, date: dateStr,
          description: formData.customerName, document_type: 'invoice',
          // No exclude_id on the Create page - we're not editing an existing
          // invoice. (Referencing an `isEditing` variable that doesn't exist
          // here threw a ReferenceError and silently broke Save - bug fix.)
          exclude_id: undefined,
        });
        if (!shouldProceed) { setLoading(false); return; }
      }

      // First, auto-create any new products/services
      const newProducts = [];
      const newRevenueAccounts = [];
      for (const item of validItems) {
        if (item.isNew && !item.productId) {
          try {
            const response = await axios.post(`${API}/products-services`, {
              name: item.description,
              type: 'service',
              price: parseFloat(item.amount),
              taxable: true,
              create_revenue_gl: item.createRevenueGL || false
            });
            newProducts.push(response.data);
            item.productId = response.data.id;
            // CRITICAL: Update the revenue GL code from the newly created product
            // This ensures the invoice journal posts to the correct revenue account
            if (response.data.revenue_gl_code) {
              item.revenueGlCode = response.data.revenue_gl_code;
            }
            if (item.createRevenueGL && response.data.has_dedicated_gl) {
              newRevenueAccounts.push({
                name: response.data.name,
                glCode: response.data.revenue_gl_code,
                glName: response.data.revenue_gl_name
              });
            }
          } catch (err) {
            console.log('Product auto-creation:', err);
          }
        }
      }
      
      if (newProducts.length > 0) {
        toast.success(`${newProducts.length} new item(s) added to your catalog`);
      }
      
      if (newRevenueAccounts.length > 0) {
        toast.success(`${newRevenueAccounts.length} new revenue account(s) created in Chart of Accounts`, {
          description: newRevenueAccounts.map(a => `${a.glCode} - ${a.glName}`).join(', ')
        });
      }

      // Create the invoice
      // 'save' = issue immediately (post journal), 'draft' = save without posting
      let total = calculateTotal();
      let subtotal = calculateSubtotal(); // This is the FACE VALUE
      let taxTotal = calculateTotalTax();
      let faceValue = calculateFaceValue();
      
      // BANK FEED OVERRIDE: Ensure the net receivable matches the bank amount exactly
      // The bank amount is sacred - VAT and WHT must be computed WITHIN it
      if (isFromBankFeed && lockedAmount > 0) {
        const bankAmount = lockedAmount;
        
        if (customerIsWhtAgent && whtRate > 0) {
          // When WHT is applied, back-calculate so net_receivable = bank_amount
          // Formula: face_value = bank_amount / (1 + vat_rate - wht_rate)
          const effectiveVatRate = (formData.lineItems[0]?.taxRate || 0) / 100;
          const effectiveWhtRate = whtRate / 100;
          const divisor = 1 + effectiveVatRate - effectiveWhtRate;
          
          if (divisor > 0) {
            faceValue = Math.round(bankAmount / divisor * 100) / 100;
            taxTotal = Math.round(faceValue * effectiveVatRate * 100) / 100;
            total = Math.round((faceValue + taxTotal) * 100) / 100;
          }
        } else {
          // No WHT - ensure total = bank_amount exactly (fix rounding)
          total = bankAmount;
          faceValue = calculateFaceValue();
          taxTotal = Math.round((total - faceValue) * 100) / 100;
        }
      }
      
      // Period lock check for bank feed transactions (skip for drafts - no journal posting)
      if (isFromBankFeed && action !== 'draft' && !periodLockOverrideRef.current) {
        const dateStr = format(invoiceDate, 'yyyy-MM-dd');
        pendingSubmitActionRef.current = action;
        const locked = await checkPeriodLock(
          dateStr,
          async () => {
            // Post to original date - override lock
            periodLockOverrideRef.current = true;
            periodLockOverrideDateRef.current = null;
            await handleSubmit({ preventDefault: () => {} }, pendingSubmitActionRef.current);
            periodLockOverrideRef.current = false;
          },
          async () => {
            // Post to today's date
            periodLockOverrideRef.current = true;
            periodLockOverrideDateRef.current = new Date().toISOString().split('T')[0];
            await handleSubmit({ preventDefault: () => {} }, pendingSubmitActionRef.current);
            periodLockOverrideRef.current = false;
            periodLockOverrideDateRef.current = null;
          }
        );
        if (locked) { setLoading(false); return; }
      }
      
      const effectiveInvoiceDate = periodLockOverrideDateRef.current || format(invoiceDate, 'yyyy-MM-dd');
      // iter971k-X · In edit mode, target the specific invoice with PUT. Else create-new with POST.
      const invoiceApiUrl = isEditMode
        ? (periodLockOverrideRef.current
            ? `${API}/invoices/${editInvoiceId}?allow_locked_period=true`
            : `${API}/invoices/${editInvoiceId}`)
        : (periodLockOverrideRef.current
            ? `${API}/invoices?allow_locked_period=true`
            : `${API}/invoices`);
      const invoiceHttpMethod = isEditMode ? 'put' : 'post';

      const invoiceResponse = await axios[invoiceHttpMethod](invoiceApiUrl, {
        customer_name: formData.customerName,
        customer_email: formData.customerEmail || null,
        project_id: invoiceProjectId || (editInvoiceId ? '' : null),
        description: formData.description || validItems[0]?.description || 'Invoice',
        invoice_number: formData.invoiceNumber?.trim() || null,  // iter971k-W · null → server auto-generate
        amount: faceValue,  // Face value (pre-tax amount for revenue recognition)
        subtotal: faceValue,
        face_value: faceValue, // Explicit face value field
        tax_amount: taxTotal,  // Frontend-calculated tax
        vat_mode: vatMode, // 'exclusive' or 'inclusive' - for reference
        currency: invoiceCurrency || getBaseCurrency(),
        fx_rate: invoiceCurrency && invoiceCurrency !== getBaseCurrency() ? fxRate : null,
        fx_rate_source: invoiceCurrency && invoiceCurrency !== getBaseCurrency() ? fxRateSource : null,
        base_currency: getBaseCurrency(),
        base_currency_amount: invoiceCurrency && invoiceCurrency !== getBaseCurrency() ? total * fxRate : null,
        invoice_date: effectiveInvoiceDate,  // Invoice date (may be overridden for period lock)
        due_date: format(dueDate, 'yyyy-MM-dd'),
        // Output VAT / Tax fields
        // iter 967 P0 - Send rate=0 when user chose "No Tax" on all lines so
        // the backend doesn't fall back to the country default. Mirror the
        // Edit page's per-line rate resolution.
        apply_output_vat: taxTotal > 0,
        output_vat_rate: taxTotal > 0
          ? (formData.lineItems.find(i => (i.taxRate || 0) > 0)?.taxRate || taxConfig?.taxes?.[0]?.rate || 0)
          : 0,
        output_vat_amount: taxTotal,
        // Regional second tax (CA PST/RST/QST) from the engine
        secondary_tax_label: calculateSecondaryTax() > 0 ? secondaryTaxInfo.label : null,
        secondary_tax_rate: calculateSecondaryTax() > 0 ? secondaryTaxInfo.rate : 0,
        secondary_tax_amount: Math.round(calculateSecondaryTax() * 100) / 100,
        // WHT Receivable fields (WHT is calculated on FACE VALUE, not gross)
        customer_is_wht_agent: customerIsWhtAgent,
        wht_rate: customerIsWhtAgent ? whtRate : 0,
        wht_amount: customerIsWhtAgent 
          ? (isFromBankFeed && lockedAmount > 0 
            ? Math.round(total - lockedAmount) // WHT = gross - bank_amount 
            : calculateWhtAmount()) 
          : 0,
        // Totals
        total_tax_amount: taxTotal,
        total_amount: total, // Gross amount (face value + VAT)
        net_receivable: isFromBankFeed && lockedAmount > 0 
          ? lockedAmount // Bank feed: net receivable ALWAYS = bank amount
          : (customerIsWhtAgent ? calculateNetReceivable() : total),
        line_items: validItems.map(item => {
          let lineAmount;
          if (isFromBankFeed && lockedAmount > 0 && customerIsWhtAgent && whtRate > 0) {
            // Bank feed + WHT: use back-calculated face value proportionally
            const lineGross = parseFloat(item.amount) * (parseFloat(item.quantity) || 1);
            const totalGross = validItems.reduce((s, i) => s + parseFloat(i.amount) * (parseFloat(i.quantity) || 1), 0);
            lineAmount = totalGross > 0 ? (lineGross / totalGross) * faceValue : faceValue;
          } else if (vatMode === 'inclusive') {
            lineAmount = parseFloat(item.amount) / (1 + (parseFloat(item.taxRate) || 0) / 100);
          } else {
            lineAmount = parseFloat(item.amount);
          }
          
          return {
          description: item.description,
          item_description: item.item_description || null,
          amount: lineAmount,
          quantity: parseFloat(item.quantity) || 1,
          product_id: item.productId,
          project_id: item.projectId || invoiceProjectId || null,
          image_url: item.image || null,
          // Tax per line item
          tax_code: item.taxCode || 'NONE',
          tax_rate: item.taxRate || 0,
          tax_amount: vatMode === 'exclusive' 
            ? calculateLineTax(item)
            : (parseFloat(item.amount) * (parseFloat(item.quantity) || 1)) - (parseFloat(item.amount) * (parseFloat(item.quantity) || 1) / (1 + (parseFloat(item.taxRate) || 0) / 100)),
          // Revenue GL code (for product-to-GL mapping)
          revenue_account_code: item.revenueGlCode || item.matchedProduct?.revenue_gl_code || '4000',
          // Special item terms (for deferred revenue)
          special_item_terms: item.specialTerms || specialItemTerms[item.id] || null
        };
        }),
        bank_account_id: selectedBankId || null,
        supporting_documents: supportingDocs.map(d => d.id),
        // Payment provider selection
        payment_provider: selectedPaymentProvider !== 'bank_transfer' ? selectedPaymentProvider : null,
        generate_payment_link: selectedPaymentProvider !== 'bank_transfer' && paymentProviders[selectedPaymentProvider],
        send_email: action === 'send',
        mark_paid: action === 'mark_paid',
        // iter971k-Z · Turn 5c - user-picked payment date for the settlement
        // journal. Falls back to invoice_date when omitted.
        mark_paid_date: action === 'mark_paid' && confirmedPaymentDate
          ? format(confirmedPaymentDate, 'yyyy-MM-dd')
          : null,
        generate_stripe_link: action === 'stripe',
        save_as_draft: action === 'draft',  // New flag: true = draft, false = issue immediately
        post_immediately: action === 'save',  // New flag: explicitly post on save
        // Bank feed transaction completion - when coming from "Not Quite Right" flow
        bank_feed_transaction_id: bankFeedTransactionId || null,
        complete_bank_feed: isFromBankFeed,
        // Split transaction handling - for reversing completed transactions
        is_split_transaction: isSplitMode,
        original_bank_feed_status: originalBankFeedStatus || location.state?.bankFeedPrefill?.original_status || null,
        // Early Payment Discount fields
        enable_early_payment_discount: enableDiscount,
        discount_type: enableDiscount ? discountType : null,
        discount_percent: enableDiscount && discountType === 'percentage' ? parseFloat(discountPercent) || 0 : null,
        discount_amount: enableDiscount && discountType === 'fixed_amount' ? parseFloat(discountAmount) || 0 : null,
        discount_days: enableDiscount ? parseInt(discountDays) || 10 : null,
        // Sales Discount fields (Trade Discount at invoice creation - reduces invoice amount)
        enable_sales_discount: enableSalesDiscount,
        sales_discount_type: enableSalesDiscount ? salesDiscountType : null,
        sales_discount_percent: enableSalesDiscount && salesDiscountType === 'percentage' ? parseFloat(salesDiscountPercent) || 0 : null,
        sales_discount_amount: enableSalesDiscount && salesDiscountType === 'fixed_amount' ? parseFloat(salesDiscountAmount) || 0 : null,
        sales_discount_reason: enableSalesDiscount ? salesDiscountReason : null
      });

      const invoiceId = invoiceResponse.data.id;
      if (invoiceResponse.data.payment_posting_failed) {
        toast.error(invoiceResponse.data.payment_posting_error || 'Invoice saved, but the payment could not be recorded. Use Record Payment to retry.', { duration: 9000 });
      }

      // iter1221 (Feb 2026) - the backend attaches `payment_link_error` when the
      // auto-generated Stripe checkout session fails silently. Surface it so the
      // user isn't left with a null payment_link they don't know about.
      // iter1222 (Feb 14, 2026) - dismissible toast + edit-mode variant.
      const paymentLinkError = invoiceResponse.data.payment_link_error;
      const hasPaymentLink = !!invoiceResponse.data.payment_link;
      if (paymentLinkError && selectedPaymentProvider === 'stripe' && !hasPaymentLink && action !== 'stripe') {
        const editVariant = paymentLinkError === 'invoice_edited_regenerate_required';
        const message = editVariant
          ? 'This invoice was edited but a fresh Stripe payment link could not be generated automatically. Open the invoice and click "Retry Stripe Payment Link".'
          : 'Invoice saved but Stripe payment link could not be generated. Open the invoice and click "Generate & Copy Stripe Payment Link" to retry.';
        toast.error(message, {
          id: `invoice-payment-link-error-${invoiceId}`,
          duration: 10000,
          dismissible: true,
          closeButton: true,
          action: {
            label: 'Dismiss',
            onClick: () => toast.dismiss(`invoice-payment-link-error-${invoiceId}`),
          },
        });
      }

      // iter1086h - Phase C follow-up: any receipts uploaded on the
      // create form (via `supportingDocs`) were saved with reference_id
      // "temp". Now that we have a real invoice_id, link each doc to it
      // via the unified backbone so it shows on the Invoice detail
      // panel AND cross-links to a matched bank-feed transaction. Non
      // fatal - the invoice is already saved.
      if (invoiceId && supportingDocs && supportingDocs.length) {
        await Promise.all(supportingDocs.map(d => {
          const docId = d.id || d.document_id;
          if (!docId) return Promise.resolve();
          return axios.post(`${API}/documents/${docId}/links`, {
            entity_type: 'invoice',
            entity_id: invoiceId,
            linked_via: 'upload',
          }).catch(() => {});
        }));
      }
      
      // If from bank feed, redirect back to bank feeds after success
      if (isFromBankFeed) {
        toast.success('Invoice created and bank transaction completed!', {
          description: 'The transaction has been moved from Review Needed to In Books.'
        });
        navigate('/bank-feeds?tab=transactions&filter=pending');
        return;
      }
      
      // Show contact creation/linking feedback
      if (invoiceResponse.data.contact_id) {
        if (invoiceResponse.data.contact_auto_created) {
          toast.success(`New contact "${formData.customerName}" created and linked!`, {
            icon: '👤',
            duration: 3000
          });
        } else {
          toast.info(`Linked to existing contact "${formData.customerName}"`, {
            icon: '🔗',
            duration: 2000
          });
        }
      }

      // Handle partial payment action - create invoice then immediately record partial payment
      if (action === 'partial_payment' && invoiceId && partialPaymentAmount) {
        try {
          await axios.post(`${API}/invoices/${invoiceId}/partial-payment`, {
            amount: parseFloat(partialPaymentAmount),
            reference: partialPaymentRef || `Partial payment on creation`,
            bank_account_id: selectedBankId || null,
            // iter971k-Z · Turn 5c - user-picked payment date
            payment_date: partialPaymentDate ? format(partialPaymentDate, 'yyyy-MM-dd') : null,
          });
          toast.success(`Invoice created with partial payment of ${getLocalCurrencySymbol()}${formatDisplayNumber(partialPaymentAmount)} recorded!`);
          navigate('/invoices');
          return;
        } catch (partialErr) {
          toast.error('Invoice created but partial payment failed. You can record it from the invoice page.');
          navigate('/invoices');
          return;
        }
      }

      // iter1421 · Save-then-open-unified-modal flow. Invoice was
      // saved (no payment yet); route to the detail page where the
      // unified RecordSettlementModal opens on mount via ?openPayment=1.
      if (action === 'open_unified_after_save' && invoiceId) {
        toast.success('Invoice saved. Record the payment below.');
        navigate(`/invoices/${invoiceId}?openPayment=1`);
        return;
      }

      // If Stripe action, generate payment link
      // iter1221 (Feb 2026) - Use the Connect-aware endpoint that stores to
      // the canonical `payment_link` field (previously called the legacy
      // /stripe/create-checkout-session endpoint which stored to `payment_url`
      // and used the platform's own Stripe account instead of the merchant's).
      if (action === 'stripe' && invoiceId) {
        try {
          const stripeResponse = await axios.post(`${API}/invoices/${invoiceId}/payment-link`);
          const link = stripeResponse.data.payment_link || stripeResponse.data.payment_url || stripeResponse.data.checkout_url;
          if (link) {
            toast.success('Stripe payment link generated!');
            try {
              await navigator.clipboard.writeText(link);
              toast.info('Payment link copied to clipboard!');
            } catch (_clipErr) {
              // Non-fatal - some browsers block clipboard writes without user gesture
            }
          } else {
            toast.error('Invoice saved but no payment link returned. Please try again from the invoice detail page.');
          }
        } catch (stripeErr) {
          const msg = stripeErr?.response?.data?.detail || 'Stripe link generation failed. You can retry from the invoice detail page.';
          toast.error(`Invoice saved but ${msg}`);
        }
      }
      
      const successMessages = {
        'save': 'Invoice created and posted!',
        'draft': 'Invoice saved as draft!',
        'send': 'Invoice created and sent to customer!',
        'mark_paid': 'Invoice created and marked as paid!',
        'stripe': 'Invoice created with Stripe payment link!'
      };
      
      toast.success(successMessages[action]);
      navigate('/invoices');
    } catch (error) {
      console.error('Invoice creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
      setSaveAction('save');
    }
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'invoice_support');

    try {
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSupportingDocs(prev => [...prev, response.data]);
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const removeDoc = (docId) => {
    setSupportingDocs(prev => prev.filter(d => d.id !== docId));
  };

  const handleLineItemImageUpload = async (lineItemId, file) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    // Set uploading state for this line item
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => 
        item.id === lineItemId ? { ...item, uploadingImage: true } : item
      )
    }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('document_type', 'product_image');

      const response = await axios.post(`${API}/documents/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Update the line item with the uploaded image URL
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map(item => 
          item.id === lineItemId 
            ? { ...item, image: response.data.url || response.data.file_url, uploadingImage: false }
            : item
        )
      }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
      setFormData(prev => ({
        ...prev,
        lineItems: prev.lineItems.map(item => 
          item.id === lineItemId ? { ...item, uploadingImage: false } : item
        )
      }));
    }
  };

  const removeLineItemImage = (lineItemId) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map(item => 
        item.id === lineItemId ? { ...item, image: null } : item
      )
    }));
  };

  const handleBankAdded = () => {
    fetchBankAccounts();
  };

  // Handle AI extracted data - pre-fill the form
  const handleAIExtract = (extracted) => {
    // Pre-fill customer info
    if (extracted.customer_name) {
      setFormData(prev => ({
        ...prev,
        customerName: extracted.customer_name,
        customerEmail: extracted.customer_email || prev.customerEmail
      }));
    }
    
    // Pre-fill line items
    if (extracted.items && extracted.items.length > 0) {
      const newLineItems = extracted.items.map((item, idx) => ({
        id: idx + 1,
        description: item.description || '',
        amount: item.amount?.toString() || '',
        quantity: item.quantity || 1,
        productId: null,
        isNew: true,
        image: null,
        uploadingImage: false,
        createRevenueGL: false,
        taxCode: '',
        taxRate: 0,
        revenueGlCode: null
      }));
      setFormData(prev => ({ ...prev, lineItems: newLineItems }));
    }
    
    // Set due date if provided
    if (extracted.due_days) {
      setDueDate(addDays(new Date(), extracted.due_days));
    }
    
    // Set notes/description
    if (extracted.notes) {
      setFormData(prev => ({ ...prev, description: extracted.notes }));
    }
    
    // Hide AI mode and show the form
    setShowAIMode(false);
    
    toast.success('Form pre-filled! Review and adjust before saving.', {
      duration: 4000
    });
  };

  const { goBack } = useNavigationContext();

  // When embedded inside the Bank-Feed dialog, skip the page chrome
  // (DashboardLayout + back-arrow header) so the form fits cleanly
  // inside the host dialog.
  //
  // iter959 - replaced the inline `Chrome` component (which was redefined
  // on every render and caused React to unmount the whole form on each
  // keystroke - closing the mobile keyboard) with a direct conditional.
  const pageBody = (
    <>
      {DuplicateDialog}
      <div className={embedded ? "p-4 sm:p-6" : "p-6 lg:p-8 max-w-3xl mx-auto"} data-testid="create-invoice-container">
        {/* Header - hidden in embedded mode (the host Dialog has its own title/close). */}
        {!embedded && (
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => goBack('/invoices')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{isEditMode ? 'Edit Invoice' : 'Get Paid'}</h1>
              <p className="text-muted-foreground">{isEditMode ? `Update ${existingInvoice?.invoice_number || 'this invoice'}` : 'Create and send an invoice'}</p>
            </div>
          </div>
        </div>
        )}
          {bankFeedLocked && (
            <div className="mb-6 flex items-start gap-3 rounded-xl border border-teal-200 bg-teal-50 p-3" data-testid="invoice-bank-feed-lock-banner">
              <Lock className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-teal-900">Linked to a bank transaction</p>
                <p className="text-teal-800 text-xs mt-0.5">This invoice was created from or settled by a Bank Feed transaction. Amount, currency, line items, dates and bank account are locked to the bank data — you can still change the customer name, description and notes. To change the money side, undo the row in Bank Feeds.</p>
                <BankTxActions documentId={editInvoiceId} allowUndo onUndone={() => goBack('/invoices')} className="mt-2" />
              </div>
            </div>
          )}

        <form onSubmit={(e) => handleSubmit(e, 'save')} className="space-y-6">
          {/* iter971k-Z · Turn 5 - Edit-mode payment / status banner.
              Mirrors the legacy EditInvoicePage banner so users still see
              payment history, reconciliation locks, and remove-payment
              controls inside the unified Create-in-edit-mode view. */}
          {isEditMode && existingInvoice && (
            <EditInvoiceStatusBanner
              invoice={existingInvoice}
              api={API}
              onChanged={async () => {
                try {
                  const { data: fresh } = await axios.get(`${API}/invoices/${editInvoiceId}`);
                  setExistingInvoice(fresh);
                } catch (_e) { /* silent: banner will retry on next action */ }
              }}
              getLocalCurrencySymbol={getLocalCurrencySymbol}
              formatDisplayNumber={formatDisplayNumber}
            />
          )}
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Who's paying you?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* iter971k-W · Invoice number override - blank = auto INV-##### */}
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  placeholder="e.g. INV-00001"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                  className="h-12"
                  data-testid="input-invoice-number"
                />
                <p className="text-xs text-muted-foreground">
                  Auto-generated. Edit to use your own (must be unique).
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerName">Customer Name *</Label>
                <div className="relative">
                  <Input
                    id="customerName"
                    placeholder="e.g. Chidi Electronics - Start typing to search contacts"
                    value={formData.customerName}
                    onChange={(e) => {
                      setFormData({ ...formData, customerName: e.target.value });
                      setSelectedCustomerId(null);
                      // Search contacts as user types
                      if (e.target.value.length >= 2) {
                        searchCustomerContacts(e.target.value);
                      } else {
                        setCustomerSuggestions([]);
                      }
                    }}
                    className="h-12"
                    data-testid="input-customer-name"
                  />
                  {/* Customer Suggestions Dropdown */}
                  {customerSuggestions.length > 0 && formData.customerName && (
                    <div className="absolute left-0 right-0 top-full z-20 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                      {customerSuggestions.map((customer) => (
                        <button
                          key={customer.id}
                          type="button"
                          onClick={() => selectCustomer(customer)}
                          className="w-full px-3 py-2 flex items-center justify-between hover:bg-muted transition-colors text-left text-sm"
                        >
                          <div>
                            <span className="font-medium">{customer.name}</span>
                            {customer.company_name && (
                              <span className="text-xs text-muted-foreground ml-2">({customer.company_name})</span>
                            )}
                            {customer.email && (
                              <span className="block text-xs text-muted-foreground">{customer.email}</span>
                            )}
                          </div>
                          <span className="text-xs px-2 py-0.5 rounded bg-[#108981]/10 text-[#108981]">
                            Customer
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchingCustomers && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
                <ContactTaxProfilePrompt contactId={selectedCustomerId} contactName={formData.customerName} contactType="customer"
                  onContactSaved={(c) => { if (c?.id) selectCustomer(c); }} className="mt-2" />
                {selectedCustomerId && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-[#108981] flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Linked to existing contact
                    </p>
                    {/* iter971k-Z · Turn 4 - inline Edit Contact */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setContactEditOpen(true)}
                      className="h-7 text-xs"
                      data-testid="btn-edit-linked-customer"
                    >
                      Edit contact
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="customerEmail">Customer Email (for sending invoice)</Label>
                <Input
                  id="customerEmail"
                  type="email"
                  placeholder="customer@example.com"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                  className="h-12"
                  data-testid="input-customer-email"
                />
              </div>
            </CardContent>
          </Card>

          {/* FX Rate Picker - Show when foreign currency might be used */}
          <div className={financeLockClass} inert={financeLocked || undefined}>
          <FXRatePicker
            baseCurrency={getBaseCurrency()}
            rateDate={invoiceDate instanceof Date ? format(invoiceDate, 'yyyy-MM-dd') : invoiceDate}
            selectedCurrency={invoiceCurrency || getBaseCurrency()}
            onCurrencyChange={(currency) => {
              setInvoiceCurrency(currency);
              if (currency === getBaseCurrency()) {
                setFxRate(1);
                setFxRateSource('live');
              } else {
                // Auto-suggest bank account matching the selected currency
                const matchingBank = bankAccounts.find(b => b.currency === currency);
                if (matchingBank) {
                  setSelectedBankId(matchingBank.id);
                }
              }
            }}
            fxRate={fxRate}
            onRateChange={(rate) => {
              setFxRate(rate);
            }}
            amount={calculateTotal()}
            showConversion={invoiceCurrency && invoiceCurrency !== getBaseCurrency()}
            disabled={loading || financeLocked}
            initialRateSource={fxRateSource || 'live'}
          />
          </div>
          
          {/* Currency mismatch warning */}
          {invoiceCurrency && invoiceCurrency !== getBaseCurrency() && selectedBankId && (
            (() => {
              const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
              const bankCurrency = selectedBank?.currency;
              if (bankCurrency && bankCurrency !== invoiceCurrency) {
                const matchingBanks = bankAccounts.filter(b => b.source !== 'stripe' && b.currency === invoiceCurrency);
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Currency mismatch detected</p>
                      <p className="text-amber-700">
                        Invoice is in {invoiceCurrency} but selected bank account is in {bankCurrency}.
                        {matchingBanks.length > 0 ? (
                          <> Consider using a {invoiceCurrency} account for easier reconciliation.</>
                        ) : (
                          <> You may want to create a {invoiceCurrency} bank account for foreign currency transactions.</>
                        )}
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()
          )}

          {/* iter1225c - International invoice nudge: if the invoice
              currency differs from the business (functional) currency AND
              the selected bank is missing BOTH SWIFT and IBAN, gently
              prompt the merchant to add international wire details so the
              customer can actually pay. Non-blocking — the invoice sends
              either way; users can dismiss and continue. */}
          {invoiceCurrency && invoiceCurrency !== getBaseCurrency() && selectedBankId && !dismissedEnrichNudge && (
            (() => {
              const selectedBank = bankAccounts.find(b => b.id === selectedBankId);
              if (!selectedBank || selectedBank.source === 'stripe') return null;
              const hasSwift = !!(selectedBank.bic_swift || selectedBank.swift_code || selectedBank.bic);
              const hasIban = !!selectedBank.iban;
              if (hasSwift || hasIban) return null;
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-testid="international-wire-nudge">
                  <AlertCircle className="w-5 h-5 text-[#283F8B] flex-shrink-0 mt-0.5" />
                  <div className="text-sm flex-1">
                    <p className="font-medium text-[#283F8B]">Add international transfer details?</p>
                    <p className="text-slate-700 mt-0.5">
                      This looks like an international invoice ({invoiceCurrency}), but&nbsp;
                      <strong>{selectedBank.bank_name || selectedBank.account_name || 'the selected bank'}</strong>&nbsp;
                      is missing SWIFT/BIC and IBAN. Your customer will need these to send a foreign-currency wire.
                    </p>
                    <div className="mt-2 flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setEnrichBank(selectedBank)}
                        className="text-xs font-semibold text-white bg-[#283F8B] hover:bg-[#1e326f] rounded px-3 py-1.5"
                        data-testid="enrich-transfer-details-nudge-btn"
                      >
                        Add wire details
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissedEnrichNudge(true)}
                        className="text-xs text-slate-600 hover:text-slate-900 underline"
                        data-testid="dismiss-enrich-nudge"
                      >
                        Send without them
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()
          )}

          {/* Line Items - Compact Single-Line Layout */}
          <Card className={financeLockClass} inert={financeLocked || undefined} data-testid="invoice-line-items-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">What are they paying for?</CardTitle>
                <div className="flex items-center gap-2">
                  {/* AI Tax Suggestions Button - show when there are unapplied suggestions */}
                  {Object.keys(suggestedTaxes).length > 0 && formData.lineItems.some(item => suggestedTaxes[item.id] && item.taxCode !== suggestedTaxes[item.id]) && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={applyAllSuggestedTaxes}
                      className="gap-1 text-[#108981] border-[#108981]/30 hover:bg-[#108981]/10"
                      data-testid="apply-all-tax-suggestions-btn"
                    >
                      <Wand2 className="w-4 h-4" />
                      Apply AI Tax Suggestions
                    </Button>
                  )}
                  {fetchingSuggestions && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Getting suggestions...
                    </span>
                  )}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={addLineItem}
                    disabled={isNotQuiteRightMode}
                    title={isNotQuiteRightMode ? "Amount is locked in 'Not Quite Right' mode. Use Split Transaction to add multiple lines." : ""}
                    data-testid="add-line-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
              </div>
              {/* Column Headers */}
              <div className="hidden md:grid grid-cols-12 gap-3 mt-4 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <div className="col-span-4">Item / Service</div>
                <div className="col-span-1 text-center">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-center">Tax</div>
                <div className="col-span-2 text-right">Total</div>
                <div className="col-span-1 text-center"></div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <AnimatePresence>
                {formData.lineItems.map((item, index) => (
                  <motion.div 
                    key={item.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="relative"
                  >
                    {/* Single-Line Item Row - responsive: stacked on mobile, grid on desktop */}
                    <div className="md:grid md:grid-cols-12 md:gap-3 md:items-center p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors space-y-2 md:space-y-0">
                      {/* Item/Service - full width on mobile */}
                      <div className="md:col-span-4 relative">
                        <label className="text-xs text-muted-foreground md:hidden mb-1 block">Item / Service</label>
                        <Input
                          placeholder="Search or add new item..."
                          value={item.description}
                          onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                          onBlur={() => handleDescriptionBlur(item)}
                          className="h-11 text-base pr-12"
                          data-testid={`line-item-desc-${index}`}
                        />
                        {/* Status badges inside input */}
                        <div className="absolute right-2 top-1/2 md:top-1/2 -translate-y-1/2 flex items-center gap-1" style={{top: 'calc(50% + 0.5rem)'}}>
                          {item.matchedProduct && (
                            <>
                              <span className="text-[10px] bg-[#108981]/10 text-[#108981] px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                <Check className="w-2.5 h-2.5" />
                              </span>
                              {/* iter971k-Z · Turn 5 - Inline Edit Product */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setEditingProductId(item.matchedProduct.id);
                                  setEditingProductLineId(item.id);
                                  setProductEditOpen(true);
                                }}
                                className="text-[10px] bg-[#283F8B]/10 text-[#283F8B] hover:bg-[#283F8B]/20 px-1.5 py-0.5 rounded flex items-center gap-0.5 transition-colors"
                                title="Edit this product"
                                data-testid={`edit-product-${index}`}
                              >
                                <PenLine className="w-2.5 h-2.5" />
                                Edit
                              </button>
                            </>
                          )}
                          {item.isNew && !item.matchedProduct && item.description.length > 2 && (
                            <span className="text-[10px] bg-[#5FC6C5]/10 text-[#5FC6C5] px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              <Sparkles className="w-2.5 h-2.5" />
                            </span>
                          )}
                        </div>
                        {/* iter1094-invoice-item-description - dedicated
                            free-text description for THIS line item.
                            Separate from the searchable Item / Service
                            name above. Renders as a compact textarea on
                            desktop and single-line input on mobile with
                            expand-on-focus. */}
                        <div className="mt-2">
                          <Textarea
                            placeholder="Item description (optional): details, notes, specs..."
                            value={item.item_description || ''}
                            onChange={(e) => updateLineItem(item.id, 'item_description', e.target.value)}
                            rows={2}
                            className="text-sm resize-y min-h-[40px]"
                            data-testid={`line-item-description-${index}`}
                          />
                        </div>
                        {/* Revenue GL Selection - always visible once
                            COA loaded.  User can manually pick a GL,
                            accept the AI/auto-match suggestion, or
                            request a brand-new revenue GL for this item
                            (created only on save). */}
                        {revenueAccounts.length > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                                Revenue GL
                              </Label>
                              {item.createRevenueGL && (
                                <span className="text-[10px] text-[#108981] font-medium">
                                  New GL pending on save
                                </span>
                              )}
                              {!item.createRevenueGL && glSuggestions[item.id]?.confidence !== undefined && (
                                <span className="text-[10px] text-muted-foreground">
                                  {glSuggestions[item.id]?.code === (item.revenueGlCode || '')
                                    ? 'AI matched'
                                    : 'Manual'}
                                </span>
                              )}
                            </div>
                          <Select
                            value={item.createRevenueGL ? '__create_new__' : (item.revenueGlCode || glSuggestions[item.id]?.code || '4000')}
                            onValueChange={(value) => {
                              if (value === '__create_new__') {
                                // Enable the "create revenue GL" flag for this line item
                                updateLineItem(item.id, 'createRevenueGL', true);
                                // Use the suggested new name as the GL code placeholder
                                const suggestion = glSuggestions[item.id];
                                if (suggestion?.newName) {
                                  toast.success(`New revenue account "${suggestion.newName}" will be created when you save`);
                                }
                                return;
                              }
                              if (value === '__create_manual__') {
                                setShowCreateAccountModal(true);
                                return;
                              }
                              const acc = revenueAccounts.find(a => a.code === value);
                              updateLineItem(item.id, 'revenueGlCode', value);
                              updateLineItem(item.id, 'revenueGlName', acc?.name || 'Revenue');
                              updateLineItem(item.id, 'createRevenueGL', false);
                            }}
                          >
                            <SelectTrigger className="h-9 text-xs bg-background" data-testid={`line-item-gl-${index}`}>
                              <SelectValue>
                                <span className="flex items-center gap-1.5">
                                  <span className="text-muted-foreground font-mono text-[10px]">
                                    {item.createRevenueGL ? 'NEW' : (item.revenueGlCode || glSuggestions[item.id]?.code || '4000')}
                                  </span>
                                  <span className="truncate">
                                    {item.createRevenueGL
                                      ? (glSuggestions[item.id]?.newName || `${(item.description||'').trim() || 'Item'} Revenue`)
                                      : (item.revenueGlName || glSuggestions[item.id]?.name || 'Sales Revenue')}
                                  </span>
                                </span>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <div className="px-2 py-1 text-xs text-muted-foreground border-b mb-1">Revenue Account</div>
                              {/* "Create dedicated GL for this item" is always available
                                  once the user has typed a description: gives them the
                                  option even when a specific GL was matched (Iter 680). */}
                              {(glSuggestions[item.id]?.newName || (item.description?.trim().length > 2)) && (
                                <SelectItem value="__create_new__" className="text-[#108981] font-medium">
                                  <span className="flex items-center gap-2">
                                    <Plus className="w-3 h-3" />
                                    <span>Create: {glSuggestions[item.id]?.newName || `${item.description.trim()} Revenue`}</span>
                                  </span>
                                </SelectItem>
                              )}
                              {revenueAccounts.map(acc => (
                                <SelectItem key={acc.code} value={acc.code}>
                                  <span className="flex items-center gap-2">
                                    <span className="text-muted-foreground text-xs">{acc.code}</span>
                                    <span>{acc.name}</span>
                                    {glSuggestions[item.id]?.code === acc.code && (
                                      <span className="text-[#108981] text-[10px] ml-1">suggested</span>
                                    )}
                                  </span>
                                </SelectItem>
                              ))}
                              <SelectItem value="__create_manual__" className="text-[#108981] font-medium border-t">
                                <span className="flex items-center gap-2">
                                  <Plus className="w-3 h-3" />
                                  <span>Create New Account</span>
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          {/* Helper text - appears only when description is
                              empty to nudge typing for better AI matching. */}
                          {(!item.description || item.description.trim().length < 3) && (
                            <p className="text-[10px] text-muted-foreground mt-1 italic">
                              A clear description helps AI pick the right account.
                            </p>
                          )}
                          </div>
                        )}
                        {/* Per-line project allocation - overrides the invoice-level project for this line */}
                        <div className="mt-1.5 flex items-center gap-2" data-testid={`line-item-project-row-${index}`}>
                          <Label className="text-[11px] uppercase tracking-wide text-muted-foreground shrink-0">Project</Label>
                          <ProjectSelect
                            compact
                            value={item.projectId || null}
                            onChange={(pid) => updateLineItem(item.id, 'projectId', pid)}
                            createdFrom="invoice_line"
                            className="flex-1 min-w-0"
                          />
                        </div>
                      </div>
                      
                      {/* Qty, Price, Tax - 7-column grid on mobile for wider Price, inline on desktop */}
                      <div className="grid grid-cols-7 gap-2 md:contents">
                        <div className="col-span-1 md:col-span-1">
                          <label className="text-xs text-muted-foreground md:hidden mb-1 block">Qty</label>
                          <FormattedNumberInput
                            placeholder="1"
                            value={item.quantity}
                            onChange={(val) => updateLineItem(item.id, 'quantity', val)}
                            className="h-11 text-base text-center px-1"
                            allowDecimals={false}
                            min={1}
                          />
                        </div>
                        
                        <div className="col-span-4 md:col-span-2">
                          <label className="text-xs text-muted-foreground md:hidden mb-1 block">Price</label>
                          <div className="flex flex-col">
                            <CurrencyInput
                              value={item.amount}
                              onChange={(value) => updateLineItem(item.id, 'amount', value)}
                              currencySymbol={getLocalCurrencySymbol()}
                              placeholder="0.00"
                              className={`h-11 text-base text-right ${isNotQuiteRightMode ? 'bg-muted cursor-not-allowed' : ''}`}
                              disabled={isNotQuiteRightMode}
                              data-testid={`line-item-amount-${index}`}
                            />
                            {isNotQuiteRightMode && (
                              <span className="text-xs text-amber-600 text-right mt-0.5">
                                Amount locked
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="col-span-2 md:col-span-2 relative">
                          <label className="text-xs text-muted-foreground md:hidden mb-1 block">Tax</label>
                        <Select
                          value={item.taxCode || 'NONE'}
                          onValueChange={(value) => {
                            const selectedTax = getTaxOptions().find(t => t.code === value);
                            updateLineItem(item.id, 'taxCode', value);
                            updateLineItem(item.id, 'taxRate', selectedTax?.rate || 0);
                          }}
                        >
                          <SelectTrigger className={`h-11 text-sm ${suggestedTaxes[item.id] ? 'ring-2 ring-[#108981]/50 border-[#108981]' : ''}`}>
                            <SelectValue placeholder="Tax">
                              {item.taxRate > 0 ? `${item.taxRate}%` : 'No Tax'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {/* AI Suggestion Header - always show when available */}
                            {suggestedTaxes[item.id] && (
                              <div className="px-2 py-1.5 text-xs border-b border-border mb-1">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    applySuggestedTax(item.id);
                                  }}
                                  className="flex items-center gap-1 text-[#108981] hover:text-[#108981]/80 font-medium w-full"
                                >
                                  <Wand2 className="w-3 h-3" />
                                  AI Suggests: {taxConfig?.taxes?.find(t => t.code === suggestedTaxes[item.id])?.name}
                                  {item.taxCode === suggestedTaxes[item.id] && <Check className="w-3 h-3 ml-auto" />}
                                </button>
                              </div>
                            )}
                            {getTaxOptions().map((tax) => (
                              <SelectItem key={tax.code} value={tax.code}>
                                <span className="flex items-center justify-between gap-2">
                                  <span className="truncate flex items-center gap-1">
                                    {tax.code === suggestedTaxes[item.id] && <Sparkles className="w-3 h-3 text-[#108981]" />}
                                    {tax.name}
                                  </span>
                                  <span className="text-muted-foreground">{tax.rate}%</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {taxDecisions[item.id]?.definitive && (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] cursor-help ${taxDecisions[item.id].review_required ? 'text-amber-700' : 'text-slate-500'}`}
                            title={`${(taxDecisions[item.id].why || []).join('\n')}${taxDecisions[item.id].citation?.rule ? `\n\nBasis: ${taxDecisions[item.id].citation.rule} (${taxDecisions[item.id].citation.pack_id || ''})` : ''}`}
                            data-testid={`tax-why-${item.id}`}
                          >
                            <Info className="w-3 h-3" />
                            {taxDecisions[item.id].applies ? `${taxDecisions[item.id].treatment} ${taxDecisions[item.id].rate}%` : `no tax · ${taxDecisions[item.id].treatment || 'n/a'}`}{taxDecisions[item.id].review_required ? ' · review' : ''}
                          </span>
                        )}
                        {/* AI suggestion quick-apply button - always show when available */}
                        {suggestedTaxes[item.id] && (
                          <button
                            type="button"
                            onClick={() => applySuggestedTax(item.id)}
                            className={`absolute -right-6 top-1/2 -translate-y-1/2 ${item.taxCode === suggestedTaxes[item.id] ? 'text-[#108981]/50' : 'text-[#108981] hover:text-[#108981]/80'}`}
                            title={item.taxCode === suggestedTaxes[item.id] ? 'AI suggestion applied' : 'Apply AI suggestion'}
                            disabled={item.taxCode === suggestedTaxes[item.id]}
                          >
                            {item.taxCode === suggestedTaxes[item.id] ? <Check className="w-4 h-4" /> : <Wand2 className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                      </div>{/* end mobile grid */}
                      
                      {/* Total + Delete row - full width on mobile */}
                      <div className="flex items-center justify-between md:contents">
                        <div className="md:col-span-2 h-11 px-3 bg-muted rounded flex flex-col items-end justify-center flex-1 md:flex-none">
                          <span className="font-semibold text-base">
                            {getLocalCurrencySymbol()}{formatDisplayNumber(calculateLineTotal(item))}
                          </span>
                          {item.taxRate > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {vatMode === 'inclusive' ? (
                                <>incl. {getLocalCurrencySymbol()}{formatDisplayNumber(calculateLineTax(item))} tax</>
                              ) : (
                                <>+{getLocalCurrencySymbol()}{formatDisplayNumber(calculateLineTax(item))} tax</>
                              )}
                            </span>
                          )}
                        </div>
                      
                      {/* Image & Delete */}
                      <div className="md:col-span-1 flex items-center justify-center gap-1 ml-2 md:ml-0">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={(el) => imageInputRefs.current[item.id] = el}
                          onChange={(e) => handleLineItemImageUpload(item.id, e.target.files?.[0])}
                        />
                        
                        {item.image ? (
                          <div className="relative">
                            <img 
                              src={item.image} 
                              alt={item.description || 'Product'} 
                              className="w-8 h-8 object-cover rounded border border-border cursor-pointer hover:opacity-80"
                              onClick={() => imageInputRefs.current[item.id]?.click()}
                            />
                            <button
                              type="button"
                              onClick={() => removeLineItemImage(item.id)}
                              className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/90"
                            >
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => imageInputRefs.current[item.id]?.click()}
                            disabled={item.uploadingImage}
                            data-testid={`upload-image-btn-${index}`}
                          >
                            {item.uploadingImage ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ImageIcon className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                        
                        {formData.lineItems.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeLineItem(item.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      </div>{/* end Total+Delete flex wrapper */}
                    </div>
                    
                    {/* Product Suggestions Dropdown - appears below the row */}
                    {item.description.length >= 2 && !item.matchedProduct && productsServices.length > 0 && (
                      <div className="absolute left-0 right-0 top-full z-20 mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                        {productsServices
                          .filter(p => 
                            p.name.toLowerCase().includes(item.description.toLowerCase()) ||
                            item.description.toLowerCase().includes(p.name.toLowerCase().substring(0, 3))
                          )
                          .slice(0, 5)
                          .map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              onClick={() => selectProduct(item.id, product)}
                              className="w-full px-3 py-2 flex items-center gap-3 hover:bg-muted transition-colors text-left text-sm"
                            >
                              <div className={`w-6 h-6 rounded flex items-center justify-center ${
                                product.type === 'service' ? 'bg-[#283F8B]/10' : 'bg-[#108981]/10'
                              }`}>
                                {product.type === 'service' ? (
                                  <Briefcase className="w-3 h-3 text-[#283F8B]" />
                                ) : (
                                  <Package className="w-3 h-3 text-[#108981]" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">{product.name}</p>
                              </div>
                              <span className="font-semibold text-foreground text-xs">
                                {getLocalCurrencySymbol()}{product.price.toLocaleString()}
                              </span>
                            </button>
                          ))}
                      </div>
                    )}
                    
                    
                    {/* "Create dedicated revenue GL" checkbox - always shown
                        once the user has typed a description, even when a
                        specific GL was matched OR a saved product was fuzzy-
                        matched. Pre-Iter 680 this was gated by `isNew &&
                        !matchedProduct`, which silently hid the option for
                        well-matched descriptions like "Software Consulting". */}
                    {item.description.length > 2 && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-1 mx-2 p-2 bg-gradient-to-r from-[#5FC6C5]/10 to-[#108981]/10 rounded border border-[#5FC6C5]/20"
                      >
                        <label className="flex items-center gap-2 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            checked={item.createRevenueGL}
                            onChange={(e) => updateLineItem(item.id, 'createRevenueGL', e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-border text-[#108981] focus:ring-[#108981] cursor-pointer"
                            data-testid={`create-revenue-gl-${index}`}
                          />
                          <Building className="w-3 h-3 text-[#108981]" />
                          <span className="text-muted-foreground">Create dedicated revenue GL account for "{item.description}"</span>
                        </label>
                      </motion.div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              
              {/* Totals Section */}
              <div className="pt-4 mt-2 border-t border-border space-y-2">
                {/* VAT Mode Toggle */}
                <div className="flex items-center justify-between pb-2 mb-2 border-b border-dashed border-muted-foreground/20">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">VAT Mode:</span>
                    {isFromBankFeed ? (
                      <div className="flex items-center gap-1.5">
                        <span className="px-3 py-1 text-xs rounded-md bg-background text-foreground shadow-sm">Inclusive</span>
                        <span className="text-xs text-amber-600">(Locked: bank amount is VAT-inclusive)</span>
                      </div>
                    ) : (
                    <div className="flex rounded-lg bg-muted p-0.5">
                      <button
                        type="button"
                        onClick={() => setVatMode('exclusive')}
                        className={`px-3 py-1 text-xs rounded-md transition-colors ${
                          vatMode === 'exclusive' 
                            ? 'bg-background text-foreground shadow-sm' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="vat-mode-exclusive"
                      >
                        Exclusive
                      </button>
                      <button
                        type="button"
                        onClick={() => setVatMode('inclusive')}
                        className={`px-3 py-1 text-xs rounded-md transition-colors ${
                          vatMode === 'inclusive' 
                            ? 'bg-background text-foreground shadow-sm' 
                            : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid="vat-mode-inclusive"
                      >
                        Inclusive
                      </button>
                    </div>
                    )}
                  </div>
                  <div className="group relative">
                    <AlertCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                    <div className="absolute bottom-full right-0 mb-2 w-56 p-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none z-50 border border-border">
                      {isFromBankFeed ? (
                        <>Bank amount already includes VAT, so VAT is <strong>inclusive</strong> here.</>
                      ) : (
                        <><strong>Exclusive:</strong> Amounts entered are face value. VAT is added on top.<br/>
                        <strong>Inclusive:</strong> Amounts entered include VAT. System back-calculates face value.</>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Face Value (Revenue) */}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Face Value (Revenue)</span>
                  <span className="font-medium">{getLocalCurrencySymbol()}{formatDisplayNumber(calculateFaceValue())}</span>
                </div>
                
                {/* Tax Total (only show if there's tax) */}
                {calculateTotalTax() > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Output VAT</span>
                    <span className="font-medium text-amber-600">+{getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotalTax())}</span>
                  </div>
                )}
                {calculateSecondaryTax() > 0 && (
                  <div className="flex justify-between items-center text-sm" data-testid="invoice-secondary-tax-row">
                    <span className="text-muted-foreground">{secondaryTaxInfo.label} {secondaryTaxInfo.rate}% ({secondaryTaxInfo.region})</span>
                    <span className="font-medium text-amber-600">+{getLocalCurrencySymbol()}{formatDisplayNumber(calculateSecondaryTax())}</span>
                  </div>
                )}
                
                {/* Grand Total (Gross) */}
                <div className="flex justify-between items-center pt-2 border-t border-border">
                  <span className="text-lg font-medium text-foreground">Gross Amount</span>
                  <span className={`text-2xl font-bold ${enableSalesDiscount && calculateSalesDiscount() > 0 ? 'text-muted-foreground line-through' : 'text-[#283F8B]'}`}>
                    {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}
                  </span>
                </div>
                
                {/* Sales Discount Section */}
                <div className="pt-3 mt-2 border-t border-dashed border-muted-foreground/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={enableSalesDiscount}
                          onChange={(e) => setEnableSalesDiscount(e.target.checked)}
                          className="w-4 h-4 accent-[#108981] rounded"
                          data-testid="enable-sales-discount-checkbox"
                        />
                        <span className="text-sm font-medium">Apply Sales Discount</span>
                      </label>
                      <div className="group relative">
                        <AlertCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none z-50 border border-border">
                          Discount given up front. Reduces the invoice and posts to Sales Discount (4030).
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {enableSalesDiscount && (
                    <div className="space-y-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900/30">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm">Discount Type</Label>
                          <Select value={salesDiscountType} onValueChange={setSalesDiscountType}>
                            <SelectTrigger data-testid="sales-discount-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percentage">Percentage (%)</SelectItem>
                              <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          {salesDiscountType === 'percentage' ? (
                            <>
                              <Label className="text-sm">Discount %</Label>
                              <Input
                                type="number"
                                value={salesDiscountPercent}
                                onChange={(e) => setSalesDiscountPercent(e.target.value)}
                                placeholder="e.g., 10"
                                min="0"
                                max="100"
                                step="0.5"
                                data-testid="sales-discount-percent-input"
                              />
                            </>
                          ) : (
                            <>
                              <Label className="text-sm">Discount Amount</Label>
                              <FormattedNumberInput
                                value={salesDiscountAmount}
                                onChange={setSalesDiscountAmount}
                                placeholder="e.g., 5000"
                                data-testid="sales-discount-amount-input"
                              />
                            </>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm">Reason (Optional)</Label>
                        <Input
                          value={salesDiscountReason}
                          onChange={(e) => setSalesDiscountReason(e.target.value)}
                          placeholder="e.g., Bulk purchase, Loyal customer"
                          data-testid="sales-discount-reason-input"
                        />
                      </div>
                      
                      {/* Show calculated discount */}
                      <div className="flex justify-between items-center text-sm pt-2 border-t border-green-300 dark:border-green-800">
                        <span className="text-green-700 dark:text-green-400">Sales Discount</span>
                        <span className="font-medium text-green-700 dark:text-green-400">
                          -{getLocalCurrencySymbol()}{formatDisplayNumber(calculateSalesDiscount())}
                        </span>
                      </div>
                      
                      {/* Net Amount after discount */}
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-medium text-foreground">Net Amount (AR)</span>
                        <span className="text-2xl font-bold text-[#283F8B]">
                          {getLocalCurrencySymbol()}{formatDisplayNumber(calculateNetTotal())}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* WHT Receivable Section */}
                <div className="pt-3 mt-2 border-t border-dashed border-muted-foreground/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={customerIsWhtAgent}
                          onChange={(e) => {
                            setCustomerIsWhtAgent(e.target.checked);
                            setShowWhtSection(e.target.checked);
                          }}
                          className="w-4 h-4 accent-[#108981] rounded"
                        />
                        <span className="text-sm font-medium">Customer withholds WHT</span>
                      </label>
                      {whtExpectedHint && !customerIsWhtAgent && (
                        <span className="text-[11px] text-amber-700" data-testid="wht-expected-hint">Profile says this customer usually deducts WHT — tick only if they will deduct on this invoice.</span>
                      )}
                      <div className="group relative">
                        <AlertCircle className="w-4 h-4 text-muted-foreground cursor-help" />
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2 bg-popover text-popover-foreground text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none z-50 border border-border">
                          Turn on if this customer deducts withholding tax when paying you (e.g. government, large companies).
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {customerIsWhtAgent && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-3 pl-6"
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Label className="text-xs text-muted-foreground">WHT Rate (%)</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Select value={String(whtRate)} onValueChange={(v) => { whtRateTouchedRef.current = true; setWhtRate(parseFloat(v)); }}>
                              <SelectTrigger className="w-32 h-9" data-testid="invoice-wht-rate-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {![5, 10, 7.5, 15].includes(Number(whtRate)) && <SelectItem value={String(whtRate)}>{whtRate}% (Tax engine)</SelectItem>}
                                <SelectItem value="5">5% (Individual)</SelectItem>
                                <SelectItem value="10">10% (Corporate)</SelectItem>
                                <SelectItem value="7.5">7.5% (Professional)</SelectItem>
                                <SelectItem value="15">15% (Special)</SelectItem>
                              </SelectContent>
                            </Select>
                            <span className="text-xs text-muted-foreground">
                              = {getLocalCurrencySymbol()}{formatDisplayNumber(calculateWhtAmount())}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* WHT Summary */}
                      <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-900/30">
                        <div className="flex items-center gap-2 text-amber-700 dark:text-amber-500 mb-2">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">WHT Receivable</span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Face Value (Revenue)</span>
                            <span>{getLocalCurrencySymbol()}{formatDisplayNumber(calculateFaceValue())}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Output VAT</span>
                            <span>+{getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotalTax())}</span>
                          </div>
                          <div className="flex justify-between border-t border-amber-200/50 pt-1">
                            <span className="text-muted-foreground">Gross Amount (AR)</span>
                            <span>{getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}</span>
                          </div>
                          <div className="flex justify-between text-amber-600">
                            <span>WHT on Face Value ({whtRate}%)</span>
                            <span>-{getLocalCurrencySymbol()}{formatDisplayNumber(calculateWhtAmount())}</span>
                          </div>
                          <div className="flex justify-between font-medium pt-1 border-t border-amber-200 dark:border-amber-800">
                            <span>Net Receivable</span>
                            <span className="text-[#108981]">{getLocalCurrencySymbol()}{formatDisplayNumber(calculateNetReceivable())}</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          WHT is calculated on the face value ({getLocalCurrencySymbol()}{formatDisplayNumber(calculateFaceValue())}), not the VAT-inclusive amount.
                          Customer remits {getLocalCurrencySymbol()}{formatDisplayNumber(calculateWhtAmount())} to the tax authority.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Date & Due Date */}
          <Card className={financeLockClass} inert={financeLocked || undefined} data-testid="invoice-dates-card">
            <CardHeader>
              <CardTitle className="text-lg">Invoice Dates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4" data-testid="invoice-project-row">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Project (optional)</Label>
                <ProjectSelect value={invoiceProjectId} onChange={setInvoiceProjectId} createdFrom="invoice" />
                <span className="text-xs text-muted-foreground hidden md:inline">Applies to the whole invoice: you can override per line item.</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Invoice Date */}
                <div className="space-y-2">
                  <Label>Invoice Date {isFromBankFeed && <span className="text-xs text-amber-600 ml-1">(Locked)</span>}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full h-12 justify-start text-left font-normal ${isFromBankFeed ? 'opacity-70 cursor-not-allowed' : ''}`}
                        data-testid="invoice-date-btn"
                        disabled={isFromBankFeed}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(invoiceDate, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={invoiceDate}
                        onSelect={(date) => date && setInvoiceDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    {isFromBankFeed ? 'Locked to bank transaction date' : 'Date on the invoice'}
                  </p>
                </div>
                
                {/* Due Date */}
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-12 justify-start text-left font-normal"
                        data-testid="due-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(dueDate, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dueDate}
                        onSelect={(date) => date && setDueDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-muted-foreground">
                    Payment due in {Math.ceil((dueDate - invoiceDate) / (1000 * 60 * 60 * 24))} days
                  </p>
                </div>
              </div>
              
              {/* Early Payment Discount */}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="enableDiscount"
                      checked={enableDiscount}
                      onChange={(e) => setEnableDiscount(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-[#108981] focus:ring-[#108981]"
                      data-testid="enable-discount-checkbox"
                    />
                    <Label htmlFor="enableDiscount" className="text-sm font-medium cursor-pointer">
                      Offer Early Payment Discount
                    </Label>
                  </div>
                </div>
                
                {enableDiscount && (
                  <div className="space-y-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900/30">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Discount Type</Label>
                        <Select value={discountType} onValueChange={setDiscountType}>
                          <SelectTrigger data-testid="discount-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">Percentage (%)</SelectItem>
                            <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        {discountType === 'percentage' ? (
                          <>
                            <Label className="text-sm">Discount %</Label>
                            <Input
                              type="number"
                              value={discountPercent}
                              onChange={(e) => setDiscountPercent(e.target.value)}
                              placeholder="e.g., 2"
                              min="0"
                              max="100"
                              step="0.5"
                              data-testid="discount-percent-input"
                            />
                          </>
                        ) : (
                          <>
                            <Label className="text-sm">Discount Amount</Label>
                            <FormattedNumberInput
                              value={discountAmount}
                              onChange={setDiscountAmount}
                              placeholder="e.g., 5000"
                              data-testid="discount-amount-input"
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">If Paid Within (Days)</Label>
                      <Input
                        type="number"
                        value={discountDays}
                        onChange={(e) => setDiscountDays(e.target.value)}
                        placeholder="10"
                        min="1"
                        max="60"
                        data-testid="discount-days-input"
                      />
                    </div>
                    <p className="text-xs text-green-700 dark:text-green-400">
                      {discountType === 'percentage' && discountPercent
                        ? `${discountPercent}% off (${getLocalCurrencySymbol()}${formatDisplayNumber(calculateTotal() * (parseFloat(discountPercent) / 100))}) if paid within ${discountDays} days`
                        : discountType === 'fixed_amount' && discountAmount
                        ? `${getLocalCurrencySymbol()}${formatDisplayNumber(discountAmount)} off if paid within ${discountDays} days`
                        : `E.g., "2/10 Net 30" means 2% discount if paid within 10 days`}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Method & Bank Account */}
          <Card data-testid="payment-provider-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#283F8B]" />
                Payment Method
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Payment Provider Selection - moved here for UX */}
              {loadingPaymentProviders ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* iter928 - Country-context banner */}
                  {!countryProviders.stripe_supported && (
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700"
                         data-testid="country-bank-transfer-banner">
                      <strong>{business?.country}</strong> doesn't yet have a native online-payment rail.
                      Use <strong>Bank Transfer</strong> below: your customer will see your bank details and a unique reference for matching.
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>How will your customer pay?</Label>
                    <Select
                      value={selectedPaymentProvider}
                      onValueChange={setSelectedPaymentProvider}
                      data-testid="payment-provider-select"
                    >
                      <SelectTrigger data-testid="payment-provider-trigger">
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_transfer">
                          <span className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-[#283F8B]" />
                            Bank Transfer
                          </span>
                        </SelectItem>
                        {countryProviders.stripe_supported && (
                          <SelectItem value="stripe" disabled={!paymentProviders.stripe}>
                            <span className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-[#635BFF]" />
                              Stripe (Global Cards, Apple Pay, Google Pay)
                              {!paymentProviders.stripe && (
                                <span className="text-xs text-amber-600 ml-2">(Not connected)</span>
                              )}
                            </span>
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* iter928 - Stripe connected indicator */}
                  {selectedPaymentProvider === 'stripe' && paymentProviders.stripe && (
                    <div className="p-3 bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-lg">
                      <div className="flex items-center gap-2 text-[#635BFF]">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-sm font-medium">Stripe Connected</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Card, Apple Pay, Google Pay and local methods. Paid out to your linked bank.
                      </p>
                    </div>
                  )}

                  {selectedPaymentProvider === 'stripe' && !paymentProviders.stripe && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">Stripe not connected</p>
                          <p className="text-xs text-amber-700 mt-1">Connect your Stripe account in Settings to accept card payments.</p>
                          <Button type="button" variant="link" size="sm" className="px-0 h-auto text-amber-700 font-medium mt-1" onClick={() => window.open('/settings?tab=payments', '_blank')}>Go to Settings</Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!paymentProviders.stripe && selectedPaymentProvider === 'bank_transfer' && countryProviders.stripe_supported && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Link2 className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-800">Get paid faster with online payments</p>
                          <p className="text-xs text-blue-700 mt-1">Connect Stripe to let customers pay with cards, Apple Pay, Google Pay & more.</p>
                          <Button type="button" variant="link" size="sm" className="px-0 h-auto text-blue-700 font-medium mt-1" onClick={() => window.open('/settings?tab=payments', '_blank')}>Connect Stripe</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* iter1224 - Bank picker is ALWAYS available (optional) so a
                  merchant can offer bank-transfer as a fallback rail even on
                  Stripe invoices. Virtual Stripe Clearing accounts are always
                  filtered out - they're pass-through GLs, not payable rails. */}
              {(() => {
                const payoutEligibleAccounts = bankAccounts.filter(b => b.source !== 'stripe');
                const isOptional = selectedPaymentProvider === 'stripe';
                return (
                  <>
                    {/* Divider */}
                    <div className="flex items-center gap-2">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground">
                        {isOptional ? 'Also offer bank transfer (optional)' : 'Receive funds to'}
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          Bank Account
                          {isOptional && <span className="text-xs text-muted-foreground">(optional)</span>}
                          {!isOptional && invoiceCurrency && invoiceCurrency !== getBaseCurrency() && (
                            <span className="text-xs text-muted-foreground">
                              ({invoiceCurrency} accounts shown first)
                            </span>
                          )}
                        </Label>
                        <div className="flex items-center gap-2">
                          {!bankAccountsLoaded ? (
                            /* iter1224 - Skeleton placeholder until fetchBankAccounts
                                completes; prevents Radix from firing onValueChange('')
                                when the SelectContent has zero items at first paint. */
                            <div className="h-10 flex-1 bg-slate-100 animate-pulse rounded-md" data-testid="bank-account-select-loading" />
                          ) : (
                          <Select value={selectedBankId} onValueChange={setSelectedBankId} disabled={isFromBankFeed}>
                            <SelectTrigger data-testid="bank-account-select" className={isFromBankFeed ? 'opacity-70 flex-1' : 'flex-1'}>
                              <SelectValue placeholder={isOptional ? 'None - Stripe only' : 'Select bank account'} />
                            </SelectTrigger>
                            <SelectContent>
                              {invoiceCurrency && invoiceCurrency !== getBaseCurrency() && (
                                <>
                                  {payoutEligibleAccounts.filter(b => b.currency === invoiceCurrency).length > 0 && (
                                    <div className="px-2 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50">
                                      Recommended ({invoiceCurrency} accounts)
                                    </div>
                                  )}
                                  {payoutEligibleAccounts.filter(b => b.currency === invoiceCurrency).map(bank => (
                                    <SelectItem key={bank.id} value={bank.id}>
                                      <span className="flex items-center gap-2">
                                        <span className="text-emerald-600">✓</span>
                                        {bank.bank_name} - ****{bank.account_number.slice(-4)} ({bank.currency})
                                        {bank.is_default && <span className="text-xs text-muted-foreground">(Default)</span>}
                                      </span>
                                    </SelectItem>
                                  ))}
                                  {payoutEligibleAccounts.filter(b => b.currency !== invoiceCurrency).length > 0 && (
                                    <div className="px-2 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 mt-1">
                                      Other accounts (currency mismatch)
                                    </div>
                                  )}
                                  {payoutEligibleAccounts.filter(b => b.currency !== invoiceCurrency).map(bank => (
                                    <SelectItem key={bank.id} value={bank.id}>
                                      <span className="flex items-center gap-2 text-muted-foreground">
                                        {bank.bank_name} - ****{bank.account_number.slice(-4)} ({bank.currency})
                                        {bank.is_default && <span className="text-xs">(Default)</span>}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                              {(!invoiceCurrency || invoiceCurrency === getBaseCurrency()) && payoutEligibleAccounts.map(bank => (
                                <SelectItem key={bank.id} value={bank.id}>
                                  <span className="flex items-center gap-2">
                                    {bank.bank_name} - ****{bank.account_number.slice(-4)} ({bank.currency})
                                    {bank.is_default && <span className="text-xs text-muted-foreground">(Default)</span>}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          )}
                          {/* iter1224 - Clear button lives OUTSIDE the SelectContent
                              because Radix's SelectValue can't traverse raw <div>/<button>
                              children when looking up the display text of the selected item. */}
                          {isOptional && selectedBankId && bankAccountsLoaded && (
                            <button
                              type="button"
                              className="text-xs text-muted-foreground hover:text-foreground underline whitespace-nowrap"
                              onClick={() => setSelectedBankId('')}
                              data-testid="clear-bank-selection-btn"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {isOptional
                            ? 'Optional. Add a bank account so customers can also pay by transfer if they prefer.'
                            : 'Your bank details will appear on the invoice so customers can pay via bank transfer.'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-[#283F8B]"
                        onClick={() => setShowBankModal(true)}
                        data-testid="add-another-bank-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add another bank account
                      </Button>
                    </div>
                    {payoutEligibleAccounts.length === 0 && !isOptional && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="no-bank-accounts-warning">
                        <p className="text-sm text-amber-800 mb-3">
                          Add a bank account so customers can pay by transfer.
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          className="gap-2"
                          onClick={() => navigate('/settings?tab=banking')}
                          data-testid="connect-bank-settings-btn"
                        >
                          <Building className="w-4 h-4" />
                          Go to Settings &gt; Banking
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Bank Account Modal */}
          <AddBankAccountModal 
            open={showBankModal} 
            onOpenChange={setShowBankModal}
            onSuccess={handleBankAdded}
          />

          {/* iter1225c - Enrich international transfer details in-place.
              Opens AddBankAccountModal in edit mode with the currently
              selected bank pre-loaded so the merchant can add SWIFT/IBAN
              without leaving the invoice draft. */}
          <AddBankAccountModal
            open={!!enrichBank}
            onOpenChange={(o) => { if (!o) setEnrichBank(null); }}
            editingBank={enrichBank}
            onSuccess={() => {
              setEnrichBank(null);
              // refresh bank list so the nudge disappears once wire fields land
              (async () => {
                try {
                  const res = await axios.get(`${API}/bank-accounts`);
                  const list = res.data?.bank_accounts || res.data || [];
                  setBankAccounts(list);
                } catch (_) { /* ignore */ }
              })();
            }}
          />


          {/* Additional Notes */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Additional Notes (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Any additional details for your customer..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="input-description"
              />
            </CardContent>
          </Card>

          {/* Supporting Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#283F8B]" />
                Supporting Documents
                <span className="text-xs font-normal text-muted-foreground">(Optional)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Attach contracts, quotes, or other relevant documents
              </p>
              
              <input
                type="file"
                ref={docInputRef}
                onChange={handleDocUpload}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                className="hidden"
              />
              
              {supportingDocs.length > 0 && (
                <div className="space-y-2 mb-4">
                  {supportingDocs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{doc.filename}</span>
                        <span className="text-xs text-muted-foreground uppercase">{doc.file_type}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDoc(doc.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <Button
                type="button"
                variant="outline"
                onClick={() => docInputRef.current?.click()}
                disabled={uploadingDoc}
                className="gap-2"
              >
                {uploadingDoc ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload Document
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Actions - Enhanced Options */}
          <Card className="bg-gradient-to-r from-[#283F8B]/5 to-[#108981]/5">
            <CardContent className="p-6">
              {/* Bank Feed Completion - Single CTA */}
              {isFromBankFeed ? (
                <div className="space-y-3">
                  {/* Split Mode Banner */}
                  {isSplitMode && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Scissors className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-800">Split Transaction Mode</span>
                      </div>
                      <p className="text-sm text-amber-700 mb-2">
                        Split into lines, each with its own revenue account.
                      </p>
                      <div className="flex items-center justify-between bg-amber-100/50 rounded p-2">
                        <span className="text-sm text-amber-800">Expected Total:</span>
                        <span className="font-bold text-amber-800" data-testid="split-expected-total">
                          {getLocalCurrencySymbol()}{formatDisplayNumber(splitExpectedTotal)}
                          {invoiceCurrency && invoiceCurrency !== getBaseCurrency() && fxRate > 0 && fxRate !== 1 && (
                            <span className="ml-1 text-xs font-normal text-amber-700" data-testid="split-expected-total-base">
                              (≈ {getCurrencySymbol(getBaseCurrency())}{formatDisplayNumber(splitExpectedTotal * fxRate)} @ {Number(fxRate).toLocaleString(undefined, { maximumFractionDigits: 4 })})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-sm text-amber-800">Current Total (incl. tax):</span>
                        <span className={`font-bold ${Math.abs(calculateTotal() - splitExpectedTotal) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                          {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}
                        </span>
                      </div>
                      {Math.abs(calculateTotal() - splitExpectedTotal) >= 0.01 && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Total (including tax) must equal {getLocalCurrencySymbol()}{formatDisplayNumber(splitExpectedTotal)}. Remaining: {getLocalCurrencySymbol()}{formatDisplayNumber(Math.abs(splitExpectedTotal - calculateTotal()))}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Standard Bank Feed Banner - for "Not Quite Right" mode */}
                  {!isSplitMode && isNotQuiteRightMode && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">Reclassify Transaction</span>
                      </div>
                      <p className="text-sm text-blue-700 mb-2">
                        Change the revenue account. The amount stays as on your bank.
                      </p>
                      <div className="flex items-center justify-between bg-blue-100/50 rounded p-2">
                        <span className="text-sm text-blue-800">Locked Amount:</span>
                        <span className="font-bold text-blue-800">{getLocalCurrencySymbol()}{formatDisplayNumber(lockedAmount)}</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-2 flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        To split, go back and choose "Split Transaction".
                      </p>
                    </div>
                  )}
                  
                  {/* Standard Bank Feed Banner - regular mode without NQR */}
                  {!isSplitMode && !isNotQuiteRightMode && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-blue-800">
                        <strong>Bank Feed Transaction:</strong> Saving this invoice will complete the bank feed transaction and move it from "Review Needed" to "In Books".
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/bank-feeds?tab=transactions&filter=pending')}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      className="bg-[#108981] hover:bg-[#108981]/90 px-8"
                      onClick={(e) => handleSubmit(e, 'mark_paid')}
                      disabled={loading || (isSplitMode && Math.abs(calculateTotal() - splitExpectedTotal) >= 0.01)}
                      data-testid="save-complete-transaction-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : isSplitMode ? (
                        <Scissors className="w-5 h-5 mr-2" />
                      ) : (
                        <CheckCircle2 className="w-5 h-5 mr-2" />
                      )}
                      {isSplitMode ? 'Save Split & Complete Transaction' : 'Save & Complete Transaction'}
                    </Button>
                  </div>
                </div>
              ) : (
              <>
              <p className="text-sm font-medium text-muted-foreground mb-4">What would you like to do with this invoice?</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {/* Save & Post (Primary action) */}
                <Button
                  type="button"
                  className="h-16 flex flex-col items-center justify-center gap-1 bg-[#283F8B] hover:bg-[#283F8B]/90"
                  onClick={(e) => handleSubmit(e, 'save')}
                  disabled={loading}
                  data-testid="save-post-btn"
                >
                  {loading && saveAction === 'save' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">Save</span>
                    </>
                  )}
                </Button>

                {/* Save as Draft - hidden in edit-mode once invoice is issued/posted (can no longer revert to draft). */}
                {(!isEditMode || existingInvoice?.status === 'draft') && (
                <Button
                  type="button"
                  variant="outline"
                  className="h-16 flex flex-col items-center justify-center gap-1 border-2 hover:border-gray-400 hover:bg-gray-50"
                  onClick={(e) => handleSubmit(e, 'draft')}
                  disabled={loading}
                  data-testid="save-draft-btn"
                >
                  {loading && saveAction === 'draft' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-5 h-5 text-gray-500" />
                      <span className="text-sm font-medium text-gray-600">Draft</span>
                    </>
                  )}
                </Button>
                )}

                {/* Save & Send Invoice */}
                <Button
                  type="button"
                  variant="outline"
                  className="h-16 flex flex-col items-center justify-center gap-1 border-2 border-[#283F8B] hover:bg-[#283F8B]/5"
                  onClick={(e) => handleSubmit(e, 'send')}
                  disabled={loading}
                  data-testid="save-send-btn"
                >
                  {loading && saveAction === 'send' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 text-[#283F8B]" />
                      <span className="text-sm font-medium text-[#283F8B]">Send</span>
                    </>
                  )}
                </Button>

                {/* Generate Stripe Payment Link - Only show if Stripe is connected */}
                {paymentProviders.stripe && (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-16 flex flex-col items-center justify-center gap-1 border-2 border-[#635BFF] hover:bg-[#635BFF]/5"
                    onClick={(e) => handleSubmit(e, 'stripe')}
                    disabled={loading}
                    data-testid="stripe-invoice-btn"
                  >
                    {loading && saveAction === 'stripe' ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#635BFF">
                          <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
                        </svg>
                        <span className="text-sm font-medium text-[#635BFF]">Stripe</span>
                      </>
                    )}
                  </Button>
                )}

                {/* iter1421 · Unified — replaces separate
                    "Save & Mark Paid" and "Save & Part Payment"
                    buttons. Saves the invoice first, then routes to
                    the detail page where the unified
                    RecordSettlementModal opens automatically. */}
                {(!isEditMode || !(existingInvoice?.amount_paid > 0 || existingInvoice?.status === 'paid')) && (
                <Button
                  type="button"
                  className="h-16 flex flex-col items-center justify-center gap-1 bg-[#108981] hover:bg-[#108981]/90"
                  onClick={() => {
                    setPendingPaymentAction('open_unified_after_save');
                    const today = new Date();
                    const inv = invoiceDate ? new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate()) : null;
                    setConfirmedPaymentDate(inv && today < inv ? inv : today);
                    setShowPaymentDateDialog(true);
                  }}
                  disabled={loading}
                  data-testid="save-mark-paid-btn"
                >
                  {loading && saveAction === 'mark_paid' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium text-center leading-tight whitespace-normal">Save &amp; Record Payment</span>
                    </>
                  )}
                </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                {formData.customerEmail 
                  ? `Invoice will be sent to ${formData.customerEmail}` 
                  : 'Add customer email to enable sending invoice'
                }
              </p>
              </>
              )}
            </CardContent>
          </Card>

          {/* Cancel button - only show if NOT from bank feed */}
          {!isFromBankFeed && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              onClick={() => navigate('/invoices')}
            >
              Cancel
            </Button>
          </div>
          )}
        </form>
      </div>

      {/* Partial Payment Dialog */}
      <Dialog open={showPartialPaymentDialog} onOpenChange={setShowPartialPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              Create the invoice and record a partial payment in one step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Face Value */}
            <div className="p-3 bg-muted/50 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Face Value:</span>
                <span className="font-medium">{getLocalCurrencySymbol()}{formatDisplayNumber(calculateFaceValue())}</span>
              </div>
              
              {/* Output VAT if applicable */}
              {calculateTotalTax() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Output VAT:</span>
                  <span className="font-medium">+ {getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotalTax())}</span>
                </div>
              )}
              {calculateSecondaryTax() > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{secondaryTaxInfo.label} {secondaryTaxInfo.rate}%:</span>
                  <span className="font-medium">+ {getLocalCurrencySymbol()}{formatDisplayNumber(calculateSecondaryTax())}</span>
                </div>
              )}
              
              {/* Gross Amount */}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Gross Amount:</span>
                <span className="font-medium">{getLocalCurrencySymbol()}{formatDisplayNumber(calculateTotal())}</span>
              </div>
              
              {/* WHT if customer is WHT agent */}
              {customerIsWhtAgent && calculateWhtAmount() > 0 && (
                <div className="flex justify-between text-sm text-amber-700">
                  <span>WHT Deduction ({whtRate}%):</span>
                  <span className="font-medium">- {getLocalCurrencySymbol()}{formatDisplayNumber(calculateWhtAmount())}</span>
                </div>
              )}
              
              {/* Net Receivable */}
              <div className="flex justify-between pt-2 border-t">
                <span className="font-medium">Amount to Receive:</span>
                <span className="font-bold text-lg text-[#108981]">
                  {getLocalCurrencySymbol()}{formatDisplayNumber(
                    customerIsWhtAgent ? calculateNetReceivable() : calculateTotal()
                  )}
                </span>
              </div>
              
              {/* Remaining Balance after partial payment */}
              {parseFloat(partialPaymentAmount) > 0 && (
                <div className="flex justify-between text-sm pt-2 border-t mt-2">
                  <span className="text-muted-foreground">Balance after payment:</span>
                  <span className="font-medium text-amber-600">
                    {getLocalCurrencySymbol()}{formatDisplayNumber(
                      (customerIsWhtAgent ? calculateNetReceivable() : calculateTotal()) - (parseFloat(partialPaymentAmount) || 0)
                    )}
                  </span>
                </div>
              )}
              
              {/* WHT Info Banner */}
              {customerIsWhtAgent && whtRate > 0 && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  <strong>WHT:</strong> Customer will deduct {whtRate}% WHT. Amount shown is net receivable.
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="partial-amount">Payment Amount Received</Label>
              <CurrencyInput
                id="partial-amount"
                value={partialPaymentAmount}
                onChange={setPartialPaymentAmount}
                placeholder="0.00"
                currencySymbol={getLocalCurrencySymbol()}
                data-testid="partial-payment-amount"
              />
              {parseFloat(partialPaymentAmount) > (customerIsWhtAgent ? calculateNetReceivable() : calculateTotal()) && (
                <p className="text-sm text-red-500">Payment cannot exceed amount to receive</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="partial-ref">Payment Reference (Optional)</Label>
              <Input
                id="partial-ref"
                value={partialPaymentRef}
                onChange={(e) => setPartialPaymentRef(e.target.value)}
                placeholder="e.g., Bank transfer, Cash payment"
                data-testid="partial-payment-ref"
              />
            </div>
            {/* iter971k-Z · Turn 5c - Payment Date picker.  Defaults to today
                but the user must be able to backdate/forward-date the
                journal posting. */}
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start text-left font-normal h-11"
                    data-testid="partial-payment-date-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2 opacity-60" />
                    {partialPaymentDate ? format(partialPaymentDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={partialPaymentDate}
                    onSelect={(d) => d && setPartialPaymentDate(d)}
                    /* iter971k-Z · Turn 5d - Payment date cannot be earlier
                       than the invoice date (protects GL period integrity). */
                    disabled={(d) => invoiceDate && d < new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate())}
                    initialFocus
                    data-testid="partial-payment-date-calendar"
                  />
                </PopoverContent>
              </Popover>
              <p className="text-[11px] text-muted-foreground">
                This date is used for the GL journal posting and appears on the receipt.
                {invoiceDate ? ` Cannot be earlier than the invoice date (${format(invoiceDate, 'PPP')}).` : ''}
              </p>
            </div>
            {bankAccounts.filter(b => b.source !== 'stripe').length > 0 && (
              <div className="space-y-2">
                <Label>Deposit to Bank Account</Label>
                <Select value={selectedBankId} onValueChange={setSelectedBankId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.filter(b => b.source !== 'stripe').map(bank => (
                      <SelectItem key={bank.id} value={bank.id}>
                        {bank.account_name} ({bank.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPartialPaymentDialog(false);
                setPartialPaymentAmount('');
                setPartialPaymentRef('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                setShowPartialPaymentDialog(false);
                handleSubmit(e, 'partial_payment');
              }}
              disabled={
                !partialPaymentAmount || 
                parseFloat(partialPaymentAmount) <= 0 || 
                parseFloat(partialPaymentAmount) > (customerIsWhtAgent ? calculateNetReceivable() : calculateTotal()) ||
                loading
              }
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-partial-payment"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create & Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter971k-Z · Turn 5c - Payment Date Confirm Dialog (for Mark Paid) */}
      <Dialog open={showPaymentDateDialog} onOpenChange={setShowPaymentDateDialog}>
        <DialogContent className="sm:max-w-sm" data-testid="payment-date-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm payment date</DialogTitle>
            <DialogDescription>
              This is the date used for the GL journal posting. Defaults to today.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Payment Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-left font-normal h-11 mt-2"
                  data-testid="mark-paid-date-btn"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 opacity-60" />
                  {confirmedPaymentDate ? format(confirmedPaymentDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={confirmedPaymentDate}
                  onSelect={(d) => d && setConfirmedPaymentDate(d)}
                  disabled={(d) => invoiceDate && d < new Date(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate())}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {invoiceDate && (
              <p className="text-[11px] text-muted-foreground mt-2">
                Cannot be earlier than the invoice date ({format(invoiceDate, 'PPP')}).
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDateDialog(false)}>Cancel</Button>
            <Button
              className="bg-[#108981] hover:bg-[#108981]/90"
              onClick={(e) => {
                setShowPaymentDateDialog(false);
                const action = pendingPaymentAction || 'mark_paid';
                setPendingPaymentAction(null);
                handleSubmit(e, action);
              }}
              data-testid="confirm-payment-date-btn"
            >
              Confirm & Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Special Item Modal - For Deferred Revenue on invoices */}
      <SpecialItemModal
        open={showSpecialItemModal}
        onOpenChange={setShowSpecialItemModal}
        itemType={specialItemType}
        lineItem={formData.lineItems?.find(item => item.id === currentSpecialItemLineId)}
        amount={parseFloat(formData.lineItems?.find(item => item.id === currentSpecialItemLineId)?.amount || 0)}
        currencySymbol={getLocalCurrencySymbol()}
        formatNumber={formatDisplayNumber}
        counterpartyName={formData.customerName}
        documentType="invoice"
        onSave={(lineItemId, terms) => {
          setSpecialItemTerms(prev => ({
            ...prev,
            [lineItemId]: terms
          }));
          
          // Update the line item with special terms
          setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map(item => 
              item.id === lineItemId 
                ? { ...item, specialTerms: terms }
                : item
            )
          }));
          
          if (specialItemQueue.length > 0) {
            const [nextItem, ...remainingQueue] = specialItemQueue;
            setSpecialItemQueue(remainingQueue);
            setCurrentSpecialItemLineId(nextItem.lineItemId);
            setSpecialItemType(nextItem.itemType);
          } else {
            setShowSpecialItemModal(false);
          }
        }}
        onCancel={(lineItemId) => {
          setFormData(prev => ({
            ...prev,
            lineItems: prev.lineItems.map(item => 
              item.id === lineItemId 
                ? { ...item, specialTerms: { skipped: true } }
                : item
            )
          }));
          
          if (specialItemQueue.length > 0) {
            const [nextItem, ...remainingQueue] = specialItemQueue;
            setSpecialItemQueue(remainingQueue);
            setCurrentSpecialItemLineId(nextItem.lineItemId);
            setSpecialItemType(nextItem.itemType);
          } else {
            setShowSpecialItemModal(false);
          }
        }}
      />
      
      {/* Accounting Start Date Modal */}
      <AccountingStartDateModal 
        open={showStartDateModal} 
        onClose={() => setShowStartDateModal(false)}
        transactionType="invoice"
      />

      {/* Period Lock Warning Dialog */}
      <PeriodLockWarningDialog
        open={periodLockDialog.open}
        onClose={clearPeriodLock}
        transactionDate={periodLockDialog.transactionDate}
        lockedPeriod={periodLockDialog.lockedPeriod}
        onPostToOriginalDate={periodLockDialog.onPostToOriginalDate}
        onPostToToday={periodLockDialog.onPostToToday}
      />
      <NewAccountModal
        open={showCreateAccountModal}
        onOpenChange={setShowCreateAccountModal}
        defaultType="revenue"
        existingAccounts={revenueAccounts}
        onAccountCreated={(newAcc) => {
          setRevenueAccounts(prev => [...prev, newAcc].sort((a, b) => (a.code || '').localeCompare(b.code || '')));
        }}
      />

      {/* Bank Account Required Modal */}
      <Dialog open={showForceBankModal && !taxGateOpen} onOpenChange={(open) => { if (!open) { setShowForceBankModal(false); navigate(-1); } }}>
        <DialogContent className="sm:max-w-[500px]" onPointerDownOutside={(e) => e.preventDefault()} data-testid="force-bank-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Bank Account Required
            </DialogTitle>
            <DialogDescription className="text-base">
              You need to add at least one bank account before you can create invoices, bills, or record payments.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
              <h4 className="font-medium text-amber-800 mb-2">Why is this required?</h4>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li>Track which accounts payments are made from</li>
                <li>Reconcile bank statements with your records</li>
                <li>Generate accurate cash flow reports</li>
              </ul>
            </div>
            <Button
              className="w-full bg-[#108981] hover:bg-[#108981]/90"
              onClick={() => navigate('/settings?tab=banking')}
              data-testid="add-bank-required-btn"
            >
              <Building className="w-4 h-4 mr-2" />
              Go to Settings &gt; Banking
            </Button>
            <Button
              variant="outline"
              className="w-full mt-2"
              onClick={() => navigate(-1)}
              data-testid="go-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* iter971k-Z · Turn 4 - Inline Edit Contact modal (linked customer) */}
      {selectedCustomerId && (
        <ContactEditModal
          open={contactEditOpen}
          onOpenChange={setContactEditOpen}
          contactId={selectedCustomerId}
          onSaved={(updated) => {
            // Push the updated fields back into the invoice form
            setFormData((prev) => ({
              ...prev,
              customerName: updated.name || prev.customerName,
              customerEmail: updated.email || prev.customerEmail,
            }));
          }}
        />
      )}
      {/* iter971k-Z · Turn 5 - Inline Edit Product modal (linked line item) */}
      {editingProductId && (
        <ProductEditModal
          open={productEditOpen}
          onOpenChange={(open) => {
            setProductEditOpen(open);
            if (!open) {
              setEditingProductId(null);
              setEditingProductLineId(null);
            }
          }}
          productId={editingProductId}
          currencySymbol={getLocalCurrencySymbol()}
          onSaved={(updated) => {
            // Update the productsServices cache
            setProductsServices((prev) =>
              prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
            );
            // Update the current line item's matchedProduct + fields
            setFormData((prev) => ({
              ...prev,
              lineItems: prev.lineItems.map((li) => {
                if (li.id !== editingProductLineId) return li;
                return {
                  ...li,
                  description: updated.name || li.description,
                  amount: updated.price != null ? String(updated.price) : li.amount,
                  matchedProduct: { ...(li.matchedProduct || {}), ...updated },
                };
              }),
            }));
          }}
        />
      )}
    </>
  );

  return embedded ? pageBody : <DashboardLayout>{pageBody}</DashboardLayout>;
};

export default CreateInvoicePage;
